import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { useAuth, AuthProvider } from './AuthContext';
import { getImageUrl } from './imageHelper';
import * as XLSX from 'xlsx';
import VariantsScreen from './VariantsScreen';
import PromotionsAdmin from './PromotionsAdmin';
import AdminPickingDashboard from './AdminPickingDashboard'; // Picking Dashboard
import AdminPackingDashboard from './AdminPackingDashboard'; // Packing Dashboard
import AdminOrderDashboard from './AdminOrderDashboard'; // Order Dashboard
import DocumentFlowDashboard from './DocumentFlowDashboard'; // Document Flow Dashboard
import DocumentFlowTVDashboard from './DocumentFlowTVDashboard'; // TV Dashboard for Document Flow
import DeliveryBoxDashboard from './DeliveryBoxDashboard'; // Delivery Box Dashboard
import UnifiedDashboard from './UnifiedDashboard'; // Unified Dashboard
import BoxReceivingDashboard from './BoxReceivingDashboard'; // Box Receiving Dashboard
import HomeBannerManager from './HomeBannerManager'; // Home Banner Manager
import ProductImageManager from './ProductImageManager'; // Product Image Manager
import DriverApplicationsAdmin from './DriverApplicationsAdmin'; // Driver Applications
import DriversManagementAdmin from './DriversManagementAdmin'; // Drivers Management
import AdminDriverTrackingMap from './AdminDriverTrackingMap'; // Driver Tracking Map
import AdminDriverPayments from './AdminDriverPayments'; // Driver Payments
import AdminDeliveryManagement from './AdminDeliveryManagement'; // Delivery Management
import PasswordResetRequestsAdmin from './PasswordResetRequestsAdmin'; // Password Reset Requests
import AdminWalletManagement from './AdminWalletManagement'; // Wallet Management (Owner Only)
import AdminWalletRecon from './AdminWalletRecon'; // Wallet Recon (Owner Only)
import AdminReconciliationLog from './AdminReconciliationLog'; // BidVest Reconciliation Log (Owner Only)
import AdminSalesReport from './AdminSalesReport'; // Sales Report
import AdminOrderCapture from './AdminOrderCapture'; // Order Capture with Full Shopping Experience
import AdminNotifications from './AdminNotifications'; // Push Notifications
import VATReportScreen from './VATReportScreen'; // VAT Report
import { AuthScreen } from './AuthScreens'; // Import AuthScreen
import SubcategoryMergeModal from './SubcategoryMergeModal'; // Safe subcategory merge/rename
import { 
  Package, Grid3x3, DollarSign, TrendingUp, Plus, Edit2, 
  Trash2, Search, X, Upload, Download, LogOut, AlertCircle,
  Save, Home, Settings, BarChart3, Tag, Move, Gift, Truck, Box, ShoppingCart, FileText, CheckSquare, Square, Eye, EyeOff, ChevronDown, ChevronRight, Database, Image, Wallet, Check, Clock, Users, Key, Shield, Printer, Layers, Warehouse, MapPin, ArrowRightLeft, ArrowRight, ArrowLeft, ClipboardList, Calendar, FileSpreadsheet, Phone, MessageCircle, Minus, Send, Loader2, CreditCard, UserPlus, Monitor, Bell
} from 'lucide-react';

/**
 * ========================================
 * ADMIN DASHBOARD - FOODLINE HYPER
 * ========================================
 */

// Dashboard Stats
const DashboardStats = ({ stats }) => {
  const statsData = [
    { label: 'Total Products', value: stats.totalProducts, icon: Package, color: 'bg-blue-500' },
    { label: 'Categories', value: stats.totalCategories, icon: Grid3x3, color: 'bg-purple-500' },
    { label: 'Subcategories', value: stats.totalSubcategories, icon: Tag, color: 'bg-indigo-500' },
    { label: 'Daily Deals', value: stats.activeDeals, icon: TrendingUp, color: 'bg-red-500' },
    { label: 'In Stock', value: stats.inStock, icon: Package, color: 'bg-green-500' },
    { label: 'Out of Stock', value: stats.outOfStock, icon: AlertCircle, color: 'bg-orange-500' },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      {statsData.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <div key={index} className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-3 rounded-lg`}>
                <Icon size={24} className="text-white" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Database Reset Utility Component
const DatabaseResetUtility = ({ onRefresh }) => {
  const [resetting, setResetting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const handleClearProducts = async () => {
    if (confirmText !== 'DELETE ALL') {
      alert('Please type "DELETE ALL" to confirm');
      return;
    }

    if (!window.confirm('⚠️ FINAL WARNING: This will permanently delete ALL products. This cannot be undone!')) {
      return;
    }

    setResetting(true);
    try {
      // Delete all products
      const { error, count } = await supabase
        .from('products')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');

      if (error) throw error;

      alert(`✅ Successfully deleted all products!\n\nTotal records removed: ${count || 'all'}\n\nYou can now import fresh data.`);
      setConfirmText('');
      onRefresh();
    } catch (error) {
      console.error('Error clearing products:', error);
      alert('❌ Failed to clear products: ' + error.message);
    } finally {
      setResetting(false);
    }
  };

  const handleClearDuplicates = async () => {
    const action = window.confirm(
      '🔍 Duplicate Removal Options:\n\n' +
      'OK = Merge duplicates (update order_items to point to kept product, then delete)\n' +
      'Cancel = Just normalize names without removing duplicates\n\n' +
      'Which do you prefer?'
    );

    setResetting(true);
    try {
      // First, get all products (limited for performance)
      const { data: allProducts, error: fetchError } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (fetchError) throw fetchError;

      // Group by stock_code
      const productsByStockCode = new Map();
      const duplicatesToMerge = [];
      const updatesToMake = [];

      allProducts.forEach(product => {
        const key = product.stock_code || `no-code-${product.id}`;
        
        if (productsByStockCode.has(key)) {
          // This is a duplicate
          const keeper = productsByStockCode.get(key);
          duplicatesToMerge.push({
            keeperId: keeper.id,
            duplicateId: product.id
          });
        } else {
          // Keep the first (most recent) one
          productsByStockCode.set(key, product);
          
          // Check if name needs normalization
          const currentName = product.name;
          const hasLowerCase = /[a-z]/.test(currentName);
          const hasUpperCase = /[A-Z]/.test(currentName);
          
          // If name is ALL CAPS, convert to Title Case
          if (hasUpperCase && !hasLowerCase && currentName.length > 3) {
            const titleCase = currentName
              .toLowerCase()
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            
            updatesToMake.push({
              id: product.id,
              name: titleCase
            });
          }
        }
      });

      if (duplicatesToMerge.length === 0 && updatesToMake.length === 0) {
        alert('✅ No duplicates found and all names are properly formatted!');
        setResetting(false);
        return;
      }

      let message = `Found:\n- ${duplicatesToMerge.length} duplicate products\n- ${updatesToMake.length} names to normalize\n\n`;
      
      if (action && duplicatesToMerge.length > 0) {
        // Merge duplicates
        for (const { keeperId, duplicateId } of duplicatesToMerge) {
          // Update order_items to point to keeper
          await supabase
            .from('order_items')
            .update({ product_id: keeperId })
            .eq('product_id', duplicateId);

          // Delete duplicate
          await supabase
            .from('products')
            .delete()
            .eq('id', duplicateId);
        }
        message += `✅ Merged ${duplicatesToMerge.length} duplicates\n`;
      }

      // Normalize names
      if (updatesToMake.length > 0) {
        for (const { id, name } of updatesToMake) {
          await supabase
            .from('products')
            .update({ name })
            .eq('id', id);
        }
        message += `✅ Normalized ${updatesToMake.length} product names`;
      }

      alert(message);
      onRefresh();
    } catch (error) {
      console.error('Error processing duplicates:', error);
      alert('❌ Failed to process duplicates: ' + error.message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Clear All Products */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-red-100 rounded-lg">
            <AlertCircle size={24} className="text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-2">⚠️ Clear All Products</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will permanently delete ALL products from the database. This action cannot be undone. 
              Use this before importing a fresh dataset.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type "DELETE ALL" to confirm:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE ALL"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <button
              onClick={handleClearProducts}
              disabled={resetting || confirmText !== 'DELETE ALL'}
              className="px-6 py-2 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resetting ? 'Clearing...' : 'Clear All Products'}
            </button>
          </div>
        </div>
      </div>

      {/* Duplicate Manager */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Package size={24} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-2">🔍 Manage Duplicates & Names</h3>
            <p className="text-sm text-gray-600 mb-4">
              Find and merge duplicate products (by stock code), and normalize product names to Title Case.
            </p>
            <button
              onClick={handleClearDuplicates}
              disabled={resetting}
              className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resetting ? 'Processing...' : 'Find & Fix Duplicates'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ============================================
// ADMIN STOCK OVERVIEW SCREEN
// ============================================
const AdminStockOverview = () => {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [inventory, searchTerm, selectedWarehouse, selectedCategory]);

  const loadData = async () => {
    try {
      const { data: warehousesData } = await supabase
        .from('warehouses')
        .select('*')
        .order('name');
      setWarehouses(warehousesData || []);

      const { data: movementsData } = await supabase
        .from('stock_movements')
        .select(`
          *,
          products(id, name, barcode, stock_code, category, subcategory),
          warehouses(id, name, code),
          storage_locations(id, code, name)
        `)
        .order('created_at', { ascending: false });

      if (movementsData && movementsData.length > 0) {
        const inventoryMap = {};
        movementsData.forEach(movement => {
          const key = `${movement.warehouse_id}-${movement.location_id}-${movement.product_id}`;
          if (!inventoryMap[key]) {
            inventoryMap[key] = {
              id: key,
              product_id: movement.product_id,
              warehouse_id: movement.warehouse_id,
              location_id: movement.location_id,
              quantity: 0,
              products: movement.products,
              warehouses: movement.warehouses,
              storage_locations: movement.storage_locations
            };
          }
          if (['receipt', 'transfer_in'].includes(movement.movement_type)) {
            inventoryMap[key].quantity += movement.quantity;
          } else if (['issue', 'transfer_out'].includes(movement.movement_type)) {
            inventoryMap[key].quantity -= movement.quantity;
          }
        });

        const calculatedInventory = Object.values(inventoryMap)
          .filter(item => item.quantity > 0)
          .sort((a, b) => b.quantity - a.quantity);
        setInventory(calculatedInventory);

        const uniqueCategories = [...new Set(
          calculatedInventory.map(item => item.products?.category).filter(Boolean)
        )].sort();
        setCategories(uniqueCategories);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading inventory:', error);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...inventory];
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const product = item.products || item;
        return (
          product?.name?.toLowerCase().includes(search) ||
          product?.barcode?.toLowerCase().includes(search) ||
          product?.stock_code?.toLowerCase().includes(search)
        );
      });
    }
    if (selectedWarehouse !== 'all') {
      filtered = filtered.filter(item => item.warehouse_id === selectedWarehouse);
    }
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.products?.category === selectedCategory);
    }
    setFilteredInventory(filtered);
  };

  const getTotalQuantity = () => filteredInventory.reduce((sum, item) => sum + item.quantity, 0);
  const getTotalItems = () => filteredInventory.length;

  const exportToExcel = () => {
    const exportData = filteredInventory.map(item => {
      const product = item.products || item;
      return {
        'Product Name': product?.name || '',
        'Stock Code': product?.stock_code || '',
        'Barcode': product?.barcode || '',
        'Category': product?.category || '',
        'Warehouse': item.warehouses?.name || 'N/A',
        'Location': item.storage_locations?.code || 'N/A',
        'Quantity': item.quantity
      };
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    ws['!cols'] = [{ wch: 35 }, { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 20 }, { wch: 12 }, { wch: 10 }];
    XLSX.utils.sheet_add_aoa(ws, [
      [''], ['Summary'], ['Total Items:', getTotalItems()], ['Total Quantity:', getTotalQuantity()], ['Export Date:', new Date().toLocaleString()]
    ], { origin: `A${exportData.length + 2}` });
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Overview');
    XLSX.writeFile(wb, `Stock_Overview_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Stock Overview</h1>
        <button
          onClick={exportToExcel}
          disabled={filteredInventory.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-bold rounded-lg"
        >
          <FileSpreadsheet size={18} />
          Export Excel
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-indigo-50 rounded-lg p-4">
          <p className="text-sm text-indigo-600 font-medium">Total Items</p>
          <p className="text-3xl font-bold text-indigo-900">{getTotalItems()}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-sm text-green-600 font-medium">Total Quantity</p>
          <p className="text-3xl font-bold text-green-900">{getTotalQuantity().toLocaleString()}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, barcode, or stock code..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
          <select
            value={selectedWarehouse}
            onChange={(e) => setSelectedWarehouse(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="all">All Warehouses</option>
            {warehouses.map(wh => (
              <option key={wh.id} value={wh.id}>{wh.name}</option>
            ))}
          </select>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Product</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Stock Code</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Barcode</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Warehouse</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Location</th>
                <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase">Quantity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredInventory.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No inventory found
                  </td>
                </tr>
              ) : (
                filteredInventory.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{item.products?.name}</div>
                      {item.products?.category && (
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">{item.products.category}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900">{item.products?.stock_code || '-'}</td>
                    <td className="px-6 py-4 text-gray-600">{item.products?.barcode || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{item.warehouses?.name}</div>
                      <div className="text-xs text-gray-500">{item.warehouses?.code}</div>
                    </td>
                    <td className="px-6 py-4 text-gray-900">{item.storage_locations?.code || '-'}</td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-xl font-bold text-green-600">{item.quantity}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


// ============================================
// ADMIN GRV LIST SCREEN
// ============================================
const AdminGRVList = () => {
  const [grvs, setGrvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedGRV, setSelectedGRV] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadGRVs();
  }, [filter]);

  const loadGRVs = async () => {
    try {
      let query = supabase
        .from('goods_received_vouchers')
        .select(`
          *,
          warehouses(name, code),
          items:grv_items(count)
        `)
        .order('received_date', { ascending: false });

      if (filter === 'completed') query = query.eq('status', 'completed');
      else if (filter === 'pending') query = query.eq('status', 'pending');

      const { data, error } = await query;
      if (error) throw error;
      setGrvs(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading GRVs:', error);
      setLoading(false);
    }
  };

  const loadGRVDetails = async (grvId) => {
    try {
      const { data, error } = await supabase
        .from('goods_received_vouchers')
        .select(`
          *,
          warehouses(name, code),
          items:grv_items(
            *,
            products(name, barcode, stock_code, category),
            storage_locations(code, name)
          )
        `)
        .eq('id', grvId)
        .single();

      if (error) throw error;
      setSelectedGRV(data);
      setShowModal(true);
    } catch (error) {
      console.error('Error loading GRV details:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Goods Received Vouchers</h1>

      <div className="flex gap-2 mb-6">
        {['all', 'completed', 'pending'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg font-medium ${
              filter === f ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)} {f === 'all' ? 'GRVs' : ''}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">GRV Number</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Supplier</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Warehouse</th>
                <th className="px-6 py-3 text-center text-xs font-bold text-gray-700 uppercase">Items</th>
                <th className="px-6 py-3 text-center text-xs font-bold text-gray-700 uppercase">Status</th>
                <th className="px-6 py-3 text-center text-xs font-bold text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {grvs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">No GRVs found</td>
                </tr>
              ) : (
                grvs.map(grv => (
                  <tr key={grv.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-bold text-gray-900">{grv.grv_number}</td>
                    <td className="px-6 py-4 text-gray-600">{new Date(grv.received_date).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-gray-900">{grv.supplier || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{grv.warehouses?.name}</div>
                      <div className="text-xs text-gray-500">{grv.warehouses?.code}</div>
                    </td>
                    <td className="px-6 py-4 text-center font-bold text-gray-900">{grv.items?.[0]?.count || 0}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        grv.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {grv.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => loadGRVDetails(grv.id)}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* GRV Detail Modal */}
      {showModal && selectedGRV && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selectedGRV.grv_number}</h2>
                <p className="text-sm text-gray-600">{selectedGRV.supplier}</p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600">Date</p>
                  <p className="text-lg font-bold text-blue-900">{new Date(selectedGRV.received_date).toLocaleDateString()}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600">Warehouse</p>
                  <p className="text-lg font-bold text-green-900">{selectedGRV.warehouses?.name}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-600">Total Items</p>
                  <p className="text-lg font-bold text-purple-900">{selectedGRV.items?.length || 0}</p>
                </div>
              </div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-700">#</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-700">Product</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-700">Stock Code</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-700">Barcode</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-700">Location</th>
                    <th className="px-4 py-2 text-right text-xs font-bold text-gray-700">Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {selectedGRV.items?.map((item, idx) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3 font-bold">{idx + 1}</td>
                      <td className="px-4 py-3 font-medium">{item.products?.name}</td>
                      <td className="px-4 py-3">{item.products?.stock_code || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{item.products?.barcode || '-'}</td>
                      <td className="px-4 py-3">{item.storage_locations?.code}</td>
                      <td className="px-4 py-3 text-right font-bold text-green-600">{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================
// ADMIN STOCK REGISTER SCREEN
// ============================================
const AdminStockRegister = () => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadMovements();
  }, [filter]);

  const loadMovements = async () => {
    try {
      let query = supabase
        .from('stock_movements')
        .select(`
          *,
          products(name, barcode, stock_code),
          warehouses(name, code),
          storage_locations(code, name)
        `)
        .order('created_at', { ascending: false })
        .limit(500);

      if (filter === 'receipts') {
        query = query.in('movement_type', ['receipt', 'transfer_in', 'adjustment_in']);
      } else if (filter === 'issues') {
        query = query.in('movement_type', ['issue', 'transfer_out', 'adjustment_out']);
      }

      const { data, error } = await query;
      if (error) throw error;
      setMovements(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading movements:', error);
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const exportData = movements.map(m => ({
      'Date': new Date(m.created_at).toLocaleString(),
      'Product': m.products?.name || '',
      'Stock Code': m.products?.stock_code || '',
      'Barcode': m.products?.barcode || '',
      'Warehouse': m.warehouses?.name || '',
      'Location': m.storage_locations?.code || '',
      'Type': m.movement_type?.replace('_', ' ').toUpperCase(),
      'Reference': m.reference_type || '',
      'Quantity': m.quantity,
      'Previous Stock': m.previous_stock,
      'New Stock': m.new_stock,
      'Notes': m.notes || ''
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Register');
    XLSX.writeFile(wb, `Stock_Register_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Stock Register</h1>
        <button
          onClick={exportToExcel}
          disabled={movements.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-bold rounded-lg"
        >
          <FileSpreadsheet size={18} />
          Export Excel
        </button>
      </div>

      <div className="flex gap-2 mb-6">
        {['all', 'receipts', 'issues'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg font-medium ${
              filter === f ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f === 'all' ? 'All Movements' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Stock Code</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Warehouse</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Reference</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Qty</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">New Stock</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {movements.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">No movements found</td>
                </tr>
              ) : (
                movements.map(movement => (
                  <tr key={movement.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium">{new Date(movement.created_at).toLocaleDateString()}</div>
                      <div className="text-xs text-gray-500">{new Date(movement.created_at).toLocaleTimeString()}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{movement.products?.name}</div>
                      <div className="text-xs text-gray-500">{movement.products?.barcode}</div>
                    </td>
                    <td className="px-4 py-3 font-medium">{movement.products?.stock_code || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{movement.warehouses?.name}</div>
                      <div className="text-xs text-gray-500">{movement.storage_locations?.code}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                        ['receipt', 'transfer_in', 'adjustment_in'].includes(movement.movement_type)
                          ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {movement.movement_type?.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {movement.reference_type && (
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          movement.reference_type === 'GRV' ? 'bg-blue-100 text-blue-700' :
                          movement.reference_type === 'TRANSFER' ? 'bg-purple-100 text-purple-700' :
                          movement.reference_type === 'ORDER' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {movement.reference_type}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-bold">{movement.quantity}</td>
                    <td className="px-4 py-3 text-right font-bold">{movement.new_stock}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


// Barcode Scanner Component
const BarcodeScanner = ({ onScan, onClose }) => {
  const [manualBarcode, setManualBarcode] = useState('');

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualBarcode.trim()) {
      onScan(manualBarcode.trim());
      setManualBarcode('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Search size={28} className="text-green-600" />
              Scan or Enter Barcode
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleManualSubmit}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter Barcode
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={manualBarcode}
                onChange={(e) => setManualBarcode(e.target.value)}
                placeholder="Type or scan barcode..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                autoFocus
              />
              <button
                type="submit"
                className="px-6 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors"
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Products Screen Component - OPTIMIZED VERSION
const ProductsScreen = ({ products, categories, onRefresh, stats }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [barcodeQuery, setBarcodeQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [publishedFilter, setPublishedFilter] = useState('all'); // 'all', 'published', 'unpublished'
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  
  // Bulk selection state
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Track products that are in active promotions
  const [productsInPromotions, setProductsInPromotions] = useState(new Set());
  
  // Subcategories state
  const [subcategories, setSubcategories] = useState([]);
  const [showMergeModal, setShowMergeModal] = useState(false); // Subcategory merge/rename tool
  
  // Stock quantities from stock_movements
  const [stockQuantities, setStockQuantities] = useState({});

  // Server-side search state
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 50;
  const searchTimeoutRef = React.useRef(null);

  // Debounced server-side search function
  const performServerSearch = async (query, barcode, category, subcategory, published, page = 1) => {
    setSearchLoading(true);
    try {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let dbQuery = supabase
        .from('products')
        .select('*', { count: 'exact' });

      // Apply search filter (escape special characters)
      if (query && query.trim()) {
        const escapedQuery = query.trim().replace(/[%_]/g, '\\$&');
        dbQuery = dbQuery.or(`name.ilike.%${escapedQuery}%,stock_code.ilike.%${escapedQuery}%`);
      }

      // Apply barcode filter
      if (barcode && barcode.trim()) {
        const escapedBarcode = barcode.trim().replace(/[%_]/g, '\\$&');
        dbQuery = dbQuery.ilike('barcode', `%${escapedBarcode}%`);
      }

      // Apply category filter
      if (category && category !== 'all') {
        dbQuery = dbQuery.eq('category', category);
      }

      // Apply subcategory filter
      if (subcategory && subcategory !== 'all') {
        dbQuery = dbQuery.eq('subcategory', subcategory);
      }

      // Apply published filter
      if (published === 'published') {
        dbQuery = dbQuery.eq('published', true);
      } else if (published === 'unpublished') {
        dbQuery = dbQuery.eq('published', false);
      }

      const { data, error, count } = await dbQuery
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      setDisplayedProducts(data || []);
      setTotalCount(count || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  // Trigger search with debounce when filters change
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search by 300ms
    searchTimeoutRef.current = setTimeout(() => {
      performServerSearch(searchQuery, barcodeQuery, selectedCategory, selectedSubcategory, publishedFilter, 1);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, barcodeQuery, selectedCategory, selectedSubcategory, publishedFilter]);

  // Initial load
  useEffect(() => {
    performServerSearch('', '', 'all', 'all', 'all', 1);
  }, []);

  // Handle page change
  const handlePageChange = (newPage) => {
    performServerSearch(searchQuery, barcodeQuery, selectedCategory, selectedSubcategory, publishedFilter, newPage);
    setSelectedProducts(new Set()); // Clear selections on page change
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Print current view
  const handlePrintProducts = () => {
    const filterInfo = [
      selectedCategory !== 'all' ? `Category: ${selectedCategory}` : '',
      selectedSubcategory !== 'all' ? `Subcategory: ${selectedSubcategory}` : '',
      publishedFilter !== 'all' ? `Status: ${publishedFilter}` : '',
      searchQuery ? `Search: ${searchQuery}` : ''
    ].filter(Boolean).join(' | ') || 'All Products';

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html><head><title>Products Report</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;font-size:11px}
        h1{font-size:16px;margin-bottom:5px}
        .info{color:#666;margin-bottom:15px;font-size:10px}
        table{width:100%;border-collapse:collapse}
        th,td{border:1px solid #ddd;padding:5px;text-align:left}
        th{background:#333;color:#fff;font-size:9px}
        tr:nth-child(even){background:#f9f9f9}
        .right{text-align:right}
      </style></head><body>
      <h1>Foodline Products Report</h1>
      <p class="info">${filterInfo} | Showing: ${displayedProducts.length} of ${totalCount} products | ${new Date().toLocaleString()}</p>
      <table><thead><tr><th>#</th><th>Code</th><th>Name</th><th>Barcode</th><th>Category</th><th>Price</th><th>Stock</th><th>Published</th></tr></thead><tbody>
      ${displayedProducts.map((p,i)=>`<tr><td>${((currentPage-1)*PAGE_SIZE)+i+1}</td><td>${p.stock_code||'-'}</td><td>${p.name}</td><td>${p.barcode||'-'}</td><td>${p.category||'-'}</td><td class="right">R${(p.price||0).toFixed(2)}</td><td>${p.in_stock?'Yes':'No'}</td><td>${p.published?'✓':'✗'}</td></tr>`).join('')}
      </tbody></table>
      <script>window.onload=()=>window.print()</script>
      </body></html>
    `);
    printWindow.document.close();
  };

  useEffect(() => {
    fetchActivePromotions();
    fetchSubcategories();
  }, []);

  // Fetch stock quantities for displayed products
  const fetchStockQuantities = async (productIds) => {
    if (!productIds || productIds.length === 0) return;
    
    try {
      const { data: movementsData, error } = await supabase
        .from('stock_movements')
        .select('product_id, movement_type, quantity')
        .in('product_id', productIds);

      if (error) throw error;

      // Calculate net quantity per product
      const quantities = {};
      (movementsData || []).forEach(movement => {
        if (!quantities[movement.product_id]) {
          quantities[movement.product_id] = 0;
        }
        if (['receipt', 'transfer_in'].includes(movement.movement_type)) {
          quantities[movement.product_id] += movement.quantity;
        } else if (['issue', 'transfer_out'].includes(movement.movement_type)) {
          quantities[movement.product_id] -= movement.quantity;
        }
      });

      setStockQuantities(prev => ({ ...prev, ...quantities }));
    } catch (error) {
      console.error('Error fetching stock quantities:', error);
    }
  };

  // Fetch stock quantities when displayed products change
  useEffect(() => {
    if (displayedProducts.length > 0) {
      const productIds = displayedProducts.map(p => p.id);
      fetchStockQuantities(productIds);
    }
  }, [displayedProducts]);

  const fetchSubcategories = async () => {
    try {
      const { data, error } = await supabase
        .from('subcategories')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setSubcategories(data || []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  useEffect(() => {
    fetchActivePromotions();
  }, []);

  const fetchActivePromotions = async () => {
    try {
      const now = new Date().toISOString();
      
      // Get all active promotions
      const { data: promotions, error } = await supabase
        .from('promotions_deals')
        .select('*')
        .eq('is_active', true)
        .lte('start_date', now)
        .gte('end_date', now);

      if (error) throw error;

      // Collect all product IDs that are in active promotions
      const promotedProductIds = new Set();
      
      for (const promo of promotions || []) {
        // Handle specific products
        if (promo.applies_to === 'specific' && promo.product_ids) {
          const productIds = Array.isArray(promo.product_ids) 
            ? promo.product_ids 
            : JSON.parse(promo.product_ids || '[]');
          productIds.forEach(id => promotedProductIds.add(id));
        }
        
        // Handle variant families - only hide products specifically selected in the promotion
        if (promo.applies_to === 'variant_family' && promo.product_ids) {
          const productIds = Array.isArray(promo.product_ids) 
            ? promo.product_ids 
            : JSON.parse(promo.product_ids || '[]');
          productIds.forEach(id => promotedProductIds.add(id));
        }
      }

      setProductsInPromotions(promotedProductIds);
    } catch (error) {
      console.error('Error fetching active promotions:', error);
    }
  };

  // Get available subcategories for selected category
  const availableSubcategories = React.useMemo(() => {
    if (selectedCategory === 'all') {
      return subcategories;
    }
    // Find the category object to get its ID
    const categoryObj = categories.find(cat => cat.name === selectedCategory);
    if (!categoryObj) return [];
    
    // Filter subcategories by category_id
    return subcategories.filter(sub => sub.category_id === categoryObj.id);
  }, [selectedCategory, subcategories, categories]);

  // Reset subcategory when category changes
  useEffect(() => {
    setSelectedSubcategory('all');
  }, [selectedCategory]);

  // Use displayedProducts from server-side search (replaces old client-side filtering)
  const filteredProducts = displayedProducts;

  // Handle barcode scan
  const handleBarcodeScan = (barcode) => {
    setBarcodeQuery(barcode);
    setShowBarcodeScanner(false);
    // Also clear text search to focus on barcode
    setSearchQuery('');
  };

  // Clear barcode filter
  const clearBarcodeFilter = () => {
    setBarcodeQuery('');
  };

  // Select/Deselect all products
  const handleSelectAll = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    }
  };

  // Toggle individual product selection
  const handleToggleProduct = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  // Bulk publish products
  const handleBulkPublish = async () => {
    if (selectedProducts.size === 0) {
      alert('Please select products to publish');
      return;
    }

    if (!window.confirm(`Publish ${selectedProducts.size} selected product(s)?`)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      const productIds = Array.from(selectedProducts);
      console.log('🚀 Starting bulk publish for', productIds.length, 'products');
      console.log('📋 Product IDs:', productIds);
      
      // Process in batches to avoid query size limits
      const batchSize = 100;
      let successCount = 0;
      
      for (let i = 0; i < productIds.length; i += batchSize) {
        const batch = productIds.slice(i, i + batchSize);
        
        const { data, error, count } = await supabase
          .from('products')
          .update({ published: true })
          .in('id', batch)
          .select();

        console.log(`📦 Batch ${i / batchSize + 1} result:`, { data, error, count, batchSize: batch.length });

        if (error) {
          console.error(`❌ Error in batch ${i / batchSize + 1}:`, error);
          throw error;
        }
        
        if (!data || data.length === 0) {
          console.warn(`⚠️ No rows updated in batch ${i / batchSize + 1}. Check RLS policies!`);
          alert(`⚠️ Update may have failed due to permissions. Check if you're logged in as an admin.\n\nTry logging out and back in.`);
          return;
        }
        
        successCount += data.length;
      }

      alert(`✅ Successfully published ${successCount} product(s)!`);
      setSelectedProducts(new Set());
      onRefresh();
      // Refresh server-side search results
      performServerSearch(searchQuery, barcodeQuery, selectedCategory, selectedSubcategory, publishedFilter, currentPage);
    } catch (error) {
      console.error('❌ Error bulk publishing:', error);
      alert('Failed to publish products: ' + error.message);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk unpublish products
  const handleBulkUnpublish = async () => {
    if (selectedProducts.size === 0) {
      alert('Please select products to unpublish');
      return;
    }

    if (!window.confirm(`Unpublish ${selectedProducts.size} selected product(s)?`)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      const productIds = Array.from(selectedProducts);
      
      // Process in batches to avoid query size limits
      const batchSize = 100;
      let successCount = 0;
      
      for (let i = 0; i < productIds.length; i += batchSize) {
        const batch = productIds.slice(i, i + batchSize);
        
        const { error } = await supabase
          .from('products')
          .update({ published: false })
          .in('id', batch);

        if (error) {
          console.error(`Error in batch ${i / batchSize + 1}:`, error);
          throw error;
        }
        
        successCount += batch.length;
      }

      alert(`✅ Successfully unpublished ${successCount} product(s)!`);
      setSelectedProducts(new Set());
      onRefresh();
      // Refresh server-side search results
      performServerSearch(searchQuery, barcodeQuery, selectedCategory, selectedSubcategory, publishedFilter, currentPage);
    } catch (error) {
      console.error('Error bulk unpublishing:', error);
      alert('Failed to unpublish products: ' + error.message);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Export filtered products to Excel (fetches ALL matching products in batches)
  const handleExportExcel = async () => {
    try {
      const BATCH_SIZE = 1000;
      let allProducts = [];
      let from = 0;
      let hasMore = true;

      // Show loading state
      const exportBtn = document.querySelector('[data-export-btn]');
      if (exportBtn) {
        exportBtn.disabled = true;
        exportBtn.innerHTML = '<span class="animate-spin">↻</span> Exporting...';
      }

      // Fetch all products in batches
      while (hasMore) {
        let dbQuery = supabase
          .from('products')
          .select('*');

        // Apply text search filter
        if (searchQuery) {
          dbQuery = dbQuery.or(`name.ilike.%${searchQuery}%,stock_code.ilike.%${searchQuery}%`);
        }

        // Apply barcode filter
        if (barcodeQuery) {
          dbQuery = dbQuery.eq('barcode', barcodeQuery);
        }

        // Apply category filter
        if (selectedCategory && selectedCategory !== 'all') {
          dbQuery = dbQuery.eq('category', selectedCategory);
        }

        // Apply subcategory filter
        if (selectedSubcategory && selectedSubcategory !== 'all') {
          dbQuery = dbQuery.eq('subcategory', selectedSubcategory);
        }

        // Apply published filter
        if (publishedFilter === 'published') {
          dbQuery = dbQuery.eq('published', true);
        } else if (publishedFilter === 'unpublished') {
          dbQuery = dbQuery.eq('published', false);
        }

        // Fetch batch
        const { data: batch, error } = await dbQuery
          .order('created_at', { ascending: false })
          .range(from, from + BATCH_SIZE - 1);

        if (error) throw error;

        if (batch && batch.length > 0) {
          allProducts = [...allProducts, ...batch];
          from += BATCH_SIZE;
          
          // If we got less than BATCH_SIZE, we've reached the end
          if (batch.length < BATCH_SIZE) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      // Reset button
      if (exportBtn) {
        exportBtn.disabled = false;
        exportBtn.innerHTML = '<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg> Export Excel';
      }

      if (!allProducts || allProducts.length === 0) {
        alert('No products to export');
        return;
      }

      // Prepare data for export
      const exportData = allProducts.map((product, index) => ({
        '#': index + 1,
        'Stock Code': product.stock_code || '',
        'Product Name': product.name || '',
        'Barcode': product.barcode || '',
        'Category': product.category || '',
        'Subcategory': product.subcategory || '',
        'Price': product.price || 0,
        'Original Price': product.original_price || '',
        'Unit': product.unit || 'ea',
        'In Stock': product.in_stock ? 'Yes' : 'No',
        'Published': product.published ? 'Yes' : 'No',
        'Daily Deal': product.is_daily_deal ? 'Yes' : 'No',
        'Description': product.description || ''
      }));

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');

      // Auto-size columns
      const colWidths = [
        { wch: 5 },   // #
        { wch: 15 },  // Stock Code
        { wch: 40 },  // Product Name
        { wch: 15 },  // Barcode
        { wch: 15 },  // Category
        { wch: 15 },  // Subcategory
        { wch: 10 },  // Price
        { wch: 12 },  // Original Price
        { wch: 8 },   // Unit
        { wch: 10 },  // In Stock
        { wch: 10 },  // Published
        { wch: 10 },  // Daily Deal
        { wch: 50 },  // Description
      ];
      worksheet['!cols'] = colWidths;

      // Generate filename with filter info
      const filterParts = [];
      if (selectedCategory !== 'all') filterParts.push(selectedCategory);
      if (selectedSubcategory !== 'all') filterParts.push(selectedSubcategory);
      if (publishedFilter !== 'all') filterParts.push(publishedFilter);
      if (searchQuery) filterParts.push('search');
      
      const filterSuffix = filterParts.length > 0 ? `_${filterParts.join('_')}` : '';
      const filename = `Products${filterSuffix}_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);
      
      alert(`Successfully exported ${allProducts.length} products!`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export: ' + error.message);
    }
  };

  const handleToggleDailyDeal = async (product) => {
    try {
      const { error } = await supabase
        .from('products')
        .update({ is_daily_deal: !product.is_daily_deal })
        .eq('id', product.id);

      if (error) throw error;
      onRefresh();
    } catch (error) {
      console.error('Error toggling daily deal:', error);
      alert('Failed to update daily deal status');
    }
  };

  const handleToggleStock = async (product) => {
    try {
      const { error } = await supabase
        .from('products')
        .update({ in_stock: !product.in_stock })
        .eq('id', product.id);

      if (error) throw error;
      onRefresh();
    } catch (error) {
      console.error('Error toggling stock:', error);
      alert('Failed to update stock status');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;
      onRefresh();
      // Refresh server-side search results
      performServerSearch(searchQuery, barcodeQuery, selectedCategory, selectedSubcategory, publishedFilter, currentPage);
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product');
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Text Search */}
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search by Name/Stock Code</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          {/* Barcode Search */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Barcode Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={barcodeQuery}
                onChange={(e) => setBarcodeQuery(e.target.value)}
                placeholder="Scan or type barcode..."
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              {barcodeQuery && (
                <button
                  onClick={clearBarcodeFilter}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Barcode Scanner Button */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
            <button
              onClick={() => setShowBarcodeScanner(true)}
              className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <Search size={20} />
              Scan Barcode
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mt-4">
          {/* Filters Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 flex-1">
            {/* Category Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Subcategory Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Subcategory</label>
              <select
                value={selectedSubcategory}
                onChange={(e) => setSelectedSubcategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                disabled={selectedCategory === 'all'}
              >
                <option value="all">All Subcategories</option>
                {availableSubcategories.map(sub => (
                  <option key={sub.id} value={sub.name}>{sub.name}</option>
                ))}
              </select>
              <button
                onClick={() => setShowMergeModal(true)}
                className="mt-2 w-full px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
              >
                ⚙️ Merge / Rename Subcategories
              </button>
            </div>

            {/* Published Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select
                value={publishedFilter}
                onChange={(e) => setPublishedFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="all">All Products</option>
                <option value="published">Published Only</option>
                <option value="unpublished">Unpublished Only</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 items-end">
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap h-[42px]"
            >
              <Plus size={20} />
              Add Product
            </button>
            <button
              onClick={handleExportExcel}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap h-[42px]"
            >
              <Download size={20} />
              Export Excel
            </button>
            <button
              onClick={handlePrintProducts}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2 whitespace-nowrap h-[42px]"
            >
              <Printer size={20} />
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedProducts.size > 0 && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckSquare size={24} className="text-blue-600" />
              <span className="font-semibold text-gray-900">
                {selectedProducts.size} product{selectedProducts.size !== 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleBulkPublish}
                disabled={bulkActionLoading}
                className="px-4 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Upload size={18} />
                Publish Selected
              </button>
              <button
                onClick={handleBulkUnpublish}
                disabled={bulkActionLoading}
                className="px-4 py-2 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Download size={18} />
                Unpublish Selected
              </button>
              <button
                onClick={() => setSelectedProducts(new Set())}
                className="px-4 py-2 bg-gray-500 text-white font-bold rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2"
              >
                <X size={18} />
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Products Count Display */}
      <div className="bg-white rounded-lg shadow-sm p-3 mb-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {searchLoading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-green-500 border-t-transparent rounded-full"></span>
                Searching...
              </span>
            ) : (
              <>
                Showing <span className="font-bold text-gray-900">
                  {totalCount === 0 ? 0 : ((currentPage - 1) * PAGE_SIZE) + 1}-{Math.min(currentPage * PAGE_SIZE, totalCount)}
                </span> of <span className="font-bold text-gray-900">{totalCount}</span> product{totalCount !== 1 ? 's' : ''}
                {selectedCategory !== 'all' && ` in "${selectedCategory}"`}
                {selectedSubcategory !== 'all' && ` > "${selectedSubcategory}"`}
                {publishedFilter !== 'all' && ` (${publishedFilter})`}
                {searchQuery && ` matching "${searchQuery}"`}
                {barcodeQuery && ` with barcode "${barcodeQuery}"`}
              </>
            )}
          </p>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase hover:text-gray-700"
                  >
                    {selectedProducts.size === filteredProducts.length && filteredProducts.length > 0 ? (
                      <CheckSquare size={18} className="text-green-600" />
                    ) : (
                      <Square size={18} />
                    )}
                    Select All
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Barcode</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Published</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Daily Deal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredProducts.map(product => (
                <tr key={product.id} className={`hover:bg-gray-50 ${!product.published ? 'bg-gray-50' : ''}`}>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggleProduct(product.id)}
                      className="flex items-center"
                    >
                      {selectedProducts.has(product.id) ? (
                        <CheckSquare size={20} className="text-green-600" />
                      ) : (
                        <Square size={20} className="text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <code className="text-xs font-semibold bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {product.stock_code || 'N/A'}
                    </code>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={getImageUrl(product.image)}
                        alt={product.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div>
                        <p className="font-medium text-gray-900">{product.name}</p>
                        <p className="text-sm text-gray-500">{product.subcategory}</p>
                        {!product.published && (
                          <span className="inline-block mt-1 text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {product.barcode || 'N/A'}
                    </code>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{product.category}</td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-semibold text-gray-900">R{product.price.toFixed(2)}</p>
                      {product.original_price && (
                        <p className="text-xs text-gray-500 line-through">R{product.original_price.toFixed(2)}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      product.published
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {product.published ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-sm font-bold text-gray-900">
                        {stockQuantities[product.id] !== undefined ? stockQuantities[product.id] : '-'}
                      </span>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          (stockQuantities[product.id] !== undefined ? stockQuantities[product.id] > 0 : product.in_stock)
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {(stockQuantities[product.id] !== undefined ? stockQuantities[product.id] > 0 : product.in_stock) ? 'In Stock' : 'Out of Stock'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggleDailyDeal(product)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        product.is_daily_deal
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {product.is_daily_deal ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingProduct(product)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Edit"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || searchLoading}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                First
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || searchLoading}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={searchLoading}
                      className={`px-3 py-1 rounded text-sm ${
                        currentPage === pageNum
                          ? 'bg-green-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || searchLoading}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || searchLoading}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Barcode Scanner Modal */}
      {showBarcodeScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowBarcodeScanner(false)}
        />
      )}

      {/* Subcategory Merge / Rename tool */}
      <SubcategoryMergeModal
        isOpen={showMergeModal}
        onClose={() => setShowMergeModal(false)}
        categories={categories}
        onComplete={() => {
          fetchSubcategories();
          performServerSearch(searchQuery, barcodeQuery, selectedCategory, selectedSubcategory, publishedFilter, 1);
        }}
      />

      {/* Edit/Add Product Modal */}
      {(editingProduct || showAddModal) && (
        <ProductModal
          product={editingProduct}
          categories={categories}
          onClose={() => {
            setEditingProduct(null);
            setShowAddModal(false);
          }}
          onSave={() => {
            setEditingProduct(null);
            setShowAddModal(false);
            onRefresh();
            // Refresh server-side search results
            performServerSearch(searchQuery, barcodeQuery, selectedCategory, selectedSubcategory, publishedFilter, currentPage);
          }}
        />
      )}
    </div>
  );
};

// Product Modal
const ProductModal = ({ product, categories, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    category: product?.category || '',
    subcategory: product?.subcategory || '',
    price: product?.price || '',
    original_price: product?.original_price || '',
    stock_code: product?.stock_code || '',
    barcode: product?.barcode || '',
    image: product?.image || '',
    description: product?.description || '',
    unit: product?.unit || 'ea',
    rating: product?.rating || 4.0,
    in_stock: product?.in_stock ?? true,
    is_daily_deal: product?.is_daily_deal ?? false,
    published: product?.published ?? false
  });

  const [subcategories, setSubcategories] = useState([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (formData.category) {
      loadSubcategories(formData.category);
    }
  }, [formData.category]);

  const loadSubcategories = async (categoryName) => {
    setLoadingSubcategories(true);
    try {
      // Find the category ID from the category name
      const category = categories.find(c => c.name === categoryName);
      
      if (!category) {
        setSubcategories([]);
        return;
      }
  
      const { data, error } = await supabase
        .from('subcategories')
        .select('*')
        .eq('category_id', category.id)
        .order('name');
  
      if (error) throw error;
      setSubcategories(data || []);
    } catch (error) {
      console.error('Error loading subcategories:', error);
      setSubcategories([]);
    } finally {
      setLoadingSubcategories(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        rating: parseFloat(formData.rating)
      };

      if (product) {
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', product.id);

        if (error) throw error;
        alert(`✅ Product updated successfully!\n\nCategory: ${productData.category}\nSubcategory: ${productData.subcategory || 'None'}`);
      } else {
        const { error } = await supabase
          .from('products')
          .insert([productData]);

        if (error) throw error;
        alert('✅ Product created successfully!');
      }

      onSave();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Failed to save product: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            {product ? 'Edit Product' : 'Add New Product'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value, subcategory: '' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subcategory</label>
              <select
                value={formData.subcategory}
                onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                disabled={!formData.category || loadingSubcategories}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">
                  {!formData.category 
                    ? 'Select category first' 
                    : loadingSubcategories 
                    ? 'Loading...' 
                    : subcategories.length === 0 
                    ? 'No subcategories available' 
                    : 'Select Subcategory'}
                </option>
                {subcategories.map(sub => (
                  <option key={sub.id} value={sub.name}>{sub.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Stock Code</label>
              <input
                type="text"
                value={formData.stock_code}
                onChange={(e) => setFormData({ ...formData, stock_code: e.target.value })}
                placeholder="e.g. 15812"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Barcode</label>
              <input
                type="text"
                value={formData.barcode}
                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                placeholder="e.g. 6001087374194"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Price (R) *</label>
              <input
                type="number"
                step="0.01"
                required
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Original Price (R)</label>
              <input
                type="number"
                step="0.01"
                value={formData.original_price}
                onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                placeholder="ea, kg, pack"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={formData.rating}
                onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Image URL *</label>
              <input
                type="url"
                required
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="in_stock"
                checked={formData.in_stock}
                onChange={(e) => setFormData({ ...formData, in_stock: e.target.checked })}
                className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
              />
              <label htmlFor="in_stock" className="text-sm font-medium text-gray-700">In Stock</label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_daily_deal"
                checked={formData.is_daily_deal}
                onChange={(e) => setFormData({ ...formData, is_daily_deal: e.target.checked })}
                className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
              />
              <label htmlFor="is_daily_deal" className="text-sm font-medium text-gray-700">Daily Deal</label>
            </div>
          </div>

          {/* Published Toggle Section */}
          <div className="col-span-2 mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 mt-0.5"
              />
              <div className="flex-1">
                <label htmlFor="published" className="block text-base font-bold text-gray-900 cursor-pointer mb-1">
                  Publish to Customer App
                </label>
                <p className="text-sm text-gray-700">
                  {formData.published 
                    ? '✅ This product is VISIBLE to customers in the app' 
                    : '❌ This product is HIDDEN from customers (only visible in admin)'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-6 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Save size={20} />
              {saving ? 'Saving...' : 'Save Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Categories Screen (simplified - keeping existing structure)
const CategoriesScreen = ({ categories, onRefresh }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', image: '', icon: '🛒' });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingCategory) {
        // Update existing category
        const { error } = await supabase
          .from('categories')
          .update(formData)
          .eq('id', editingCategory.id);
        if (error) throw error;
      } else {
        // Create new category
        const { error } = await supabase
          .from('categories')
          .insert([formData]);
        if (error) throw error;
      }
      setShowModal(false);
      setEditingCategory(null);
      setFormData({ name: '', image: '', icon: '🛒' });
      onRefresh();
    } catch (error) {
      alert('Failed to save category: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Categories</h2>
        <button
          onClick={() => {
            setEditingCategory(null);
            setFormData({ name: '', image: '', icon: '🛒' });
            setShowModal(true);
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          <Plus size={20} />
          Add Category
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map(category => (
          <div key={category.id} className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-gray-900">{category.name}</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setEditingCategory(category);
                    setFormData({
                      name: category.name || '',
                      image: category.image || '',
                      icon: category.icon || '🛒'
                    });
                    setShowModal(true);
                  }}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm('Delete this category?')) return;
                    try {
                      await supabase.from('categories').delete().eq('id', category.id);
                      onRefresh();
                    } catch (error) {
                      alert('Failed to delete category');
                    }
                  }}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            {category.image && (
              <img
                src={getImageUrl(category.image)}
                alt={category.name}
                className="w-full h-32 object-cover rounded-lg"
              />
            )}
          </div>
        ))}
      </div>

      {/* Category Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingCategory ? 'Edit Category' : 'Create Category'}
              </h2>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g., Fresh Produce"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image URL (optional)
                </label>
                <input
                  type="text"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icon (emoji)
                </label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="🛒"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                    setFormData({ name: '', image: '', icon: '🛒' });
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Save size={20} />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};


const SubcategoryModal = ({ isOpen, onClose, onSave, subcategory = null, categories }) => {
  const [name, setName] = useState(subcategory?.name || '');
  const [categoryId, setCategoryId] = useState(subcategory?.category_id || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (subcategory) {
      setName(subcategory.name || '');
      setCategoryId(subcategory.category_id || '');
    } else {
      setName('');
      setCategoryId('');
    }
  }, [subcategory, isOpen]);

  const handleSave = async () => {
    if (!name || !categoryId) {
      alert('Please fill in all fields');
      return;
    }

    setSaving(true);
    try {
      if (subcategory) {
        // Update - uses category_id
        const { error } = await supabase
          .from('subcategories')
          .update({ 
            name: name.trim(), 
            category_id: categoryId 
          })
          .eq('id', subcategory.id);
        if (error) throw error;
      } else {
        // Insert - uses category_id
        const { error } = await supabase
          .from('subcategories')
          .insert({ 
            name: name.trim(), 
            category_id: categoryId 
          });
        if (error) throw error;
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving subcategory:', error);
      alert('Failed to save: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">{subcategory ? 'Edit' : 'Add'} Subcategory</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Parent Category *
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="">Select category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subcategory Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., CARBONATED SOFT DRINKS"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>

        <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-end rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-6 py-2 border text-gray-700 rounded-lg hover:bg-white font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name || !categoryId}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold disabled:opacity-50 flex items-center gap-2"
          >
            <Save size={18} />
            {saving ? 'Saving...' : (subcategory ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
};

const SubcategoriesScreen = ({ categories, onRefresh }) => {
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);

  useEffect(() => {
    loadSubcategories();
  }, []);

  const loadSubcategories = async () => {
    try {
      // Join with categories to get category name
      const { data, error } = await supabase
        .from('subcategories')
        .select(`
          *,
          category:categories(id, name)
        `)
        .order('name');

      if (error) throw error;
      setSubcategories(data || []);
    } catch (error) {
      console.error('Error loading subcategories:', error);
    }
  };

  const handleSave = () => {
    loadSubcategories();
    if (onRefresh) onRefresh();
  };

  const handleEdit = (subcategory) => {
    setEditingSubcategory(subcategory);
    setShowModal(true);
  };

  const handleDelete = async (subcategory) => {
    if (!window.confirm(`Delete "${subcategory.name}"?`)) return;

    try {
      const { error } = await supabase
        .from('subcategories')
        .delete()
        .eq('id', subcategory.id);

      if (error) throw error;
      loadSubcategories();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Failed to delete: ' + error.message);
    }
  };

  const handleAddNew = () => {
    setEditingSubcategory(null);
    setShowModal(true);
  };

  const filteredSubcategories = selectedCategory === 'all'
    ? subcategories
    : subcategories.filter(sub => sub.category_id === selectedCategory);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Subcategories</h2>
        <div className="flex gap-3">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <button
            onClick={handleAddNew}
            className="flex items-center gap-2 bg-green-600 text-white font-bold px-6 py-2 rounded-lg hover:bg-green-700"
          >
            <Plus size={20} />
            Add
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredSubcategories.map(subcategory => (
          <div key={subcategory.id} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900">{subcategory.name}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {subcategory.category?.name || 'No Category'}
                </p>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handleEdit(subcategory)}
                  className="p-2 hover:bg-green-100 rounded-lg text-green-600"
                  title="Edit"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={() => handleDelete(subcategory)}
                  className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                  title="Delete"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredSubcategories.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No subcategories found</p>
          <button
            onClick={handleAddNew}
            className="bg-green-600 text-white font-bold px-6 py-3 rounded-lg hover:bg-green-700"
          >
            Add Subcategory
          </button>
        </div>
      )}

      <SubcategoryModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingSubcategory(null);
        }}
        onSave={handleSave}
        subcategory={editingSubcategory}
        categories={categories}
      />
    </div>
  );
};



// ============================================
// WALLET APPROVALS SCREEN
// ============================================
const WalletApprovalsScreen = () => {
  const [pendingCredits, setPendingCredits] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [processing, setProcessing] = useState(null);

  const fetchWalletData = async () => {
    setLoading(true);
    try {
      // Fetch pending credits
      const { data: pending, error: pendingError } = await supabase
        .from('wallet_transactions')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (pendingError) throw pendingError;

      // Fetch all transactions (last 100)
      const { data: all, error: allError } = await supabase
        .from('wallet_transactions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (allError) throw allError;

      // Get unique user IDs, order IDs, and product IDs
      const allTransactionsData = [...(pending || []), ...(all || [])];
      const userIds = [...new Set(allTransactionsData.map(t => t.user_id).filter(Boolean))];
      const orderIds = [...new Set(allTransactionsData.map(t => t.order_id).filter(Boolean))];
      const productIds = [...new Set(allTransactionsData.map(t => t.product_id).filter(Boolean))];

      // Fetch related data separately
      const [usersResult, ordersResult, productsResult] = await Promise.all([
        userIds.length > 0 
          ? supabase.from('user_profiles').select('id, name, email, phone').in('id', userIds)
          : { data: [] },
        orderIds.length > 0
          ? supabase.from('orders').select('id, order_number').in('id', orderIds)
          : { data: [] },
        productIds.length > 0
          ? supabase.from('products').select('id, name').in('id', productIds)
          : { data: [] }
      ]);

      // Create lookup maps
      const usersMap = Object.fromEntries((usersResult.data || []).map(u => [u.id, u]));
      const ordersMap = Object.fromEntries((ordersResult.data || []).map(o => [o.id, o]));
      const productsMap = Object.fromEntries((productsResult.data || []).map(p => [p.id, p]));

      // Enrich transactions with related data
      const enrichTransaction = (t) => ({
        ...t,
        user_profile: usersMap[t.user_id] || null,
        order: ordersMap[t.order_id] || null,
        product: productsMap[t.product_id] || null
      });

      setPendingCredits((pending || []).map(enrichTransaction));
      setAllTransactions((all || []).map(enrichTransaction));
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      alert('Failed to load wallet data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWalletData();
  }, []);

  const handleApprove = async (transaction) => {
    if (!window.confirm(`Approve R${parseFloat(transaction.amount).toFixed(2)} credit for ${transaction.user_profile?.name || 'customer'}?`)) {
      return;
    }

    setProcessing(transaction.id);
    try {
      // Get current wallet balance
      const { data: lastTransaction } = await supabase
        .from('wallet_transactions')
        .select('balance_after')
        .eq('user_id', transaction.user_id)
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      const currentBalance = lastTransaction?.balance_after || 0;
      const newBalance = parseFloat(currentBalance) + parseFloat(transaction.amount);

      // Update transaction to approved
      const { error } = await supabase
        .from('wallet_transactions')
        .update({
          status: 'approved',
          balance_after: newBalance
        })
        .eq('id', transaction.id);

      if (error) throw error;

      alert(`✅ Credit approved! Customer's new balance: R${newBalance.toFixed(2)}`);
      fetchWalletData();
    } catch (error) {
      console.error('Error approving credit:', error);
      alert('Failed to approve credit: ' + error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (transaction) => {
    const reason = window.prompt('Enter rejection reason (optional):');
    
    if (reason === null) return; // User cancelled

    setProcessing(transaction.id);
    try {
      const { error } = await supabase
        .from('wallet_transactions')
        .update({
          status: 'rejected',
          description: transaction.description + (reason ? ` [Rejected: ${reason}]` : ' [Rejected]')
        })
        .eq('id', transaction.id);

      if (error) throw error;

      alert('Credit request rejected');
      fetchWalletData();
    } catch (error) {
      console.error('Error rejecting credit:', error);
      alert('Failed to reject credit: ' + error.message);
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const getReasonBadge = (reason) => {
    const styles = {
      item_unavailable: 'bg-orange-100 text-orange-800',
      admin_adjustment: 'bg-blue-100 text-blue-800',
      refund: 'bg-purple-100 text-purple-800',
      promotion_cashback: 'bg-green-100 text-green-800',
      order_payment: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      item_unavailable: 'Shortage Refund',
      admin_adjustment: 'Admin Adjustment',
      refund: 'Refund',
      promotion_cashback: 'Promo Cashback',
      order_payment: 'Order Payment'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[reason] || 'bg-gray-100 text-gray-800'}`}>
        {labels[reason] || reason}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Wallet Approvals</h1>
          <p className="text-gray-600 mt-1">Manage pending wallet credits from shortages and refunds</p>
        </div>
        <button
          onClick={fetchWalletData}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock size={24} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-yellow-700">Pending Approval</p>
              <p className="text-2xl font-bold text-yellow-800">{pendingCredits.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <DollarSign size={24} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-yellow-700">Pending Amount</p>
              <p className="text-2xl font-bold text-yellow-800">
                R{pendingCredits.reduce((sum, t) => sum + parseFloat(t.amount), 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Check size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-green-700">Approved Today</p>
              <p className="text-2xl font-bold text-green-800">
                {allTransactions.filter(t => 
                  t.status === 'approved' && 
                  new Date(t.created_at).toDateString() === new Date().toDateString()
                ).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'pending'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Pending ({pendingCredits.length})
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'all'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Transactions
        </button>
      </div>

      {/* Pending Credits Tab */}
      {activeTab === 'pending' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {pendingCredits.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">All caught up!</h3>
              <p className="text-gray-600">No pending wallet credits to approve</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pendingCredits.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{transaction.user_profile?.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">{transaction.user_profile?.phone || transaction.user_profile?.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-lg font-bold text-green-600">
                        R{parseFloat(transaction.amount).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      {getReasonBadge(transaction.reason)}
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-gray-700 max-w-xs truncate" title={transaction.description}>
                        {transaction.description}
                      </p>
                      {transaction.order?.order_number && (
                        <p className="text-xs text-gray-500 mt-1">Order #{transaction.order.order_number}</p>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {formatDate(transaction.created_at)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleApprove(transaction)}
                          disabled={processing === transaction.id}
                          className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
                        >
                          {processing === transaction.id ? '...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleReject(transaction)}
                          disabled={processing === transaction.id}
                          className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400"
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* All Transactions Tab */}
      {activeTab === 'all' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Reason</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Balance After</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {allTransactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{transaction.user_profile?.name || 'Unknown'}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      transaction.transaction_type === 'credit' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {transaction.transaction_type.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-semibold ${
                      transaction.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.transaction_type === 'credit' ? '+' : '-'}R{parseFloat(transaction.amount).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {getReasonBadge(transaction.reason)}
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(transaction.status)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {transaction.balance_after ? `R${parseFloat(transaction.balance_after).toFixed(2)}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(transaction.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ========================================
// USERS MANAGEMENT SCREEN
// ========================================
const UsersManagementScreen = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [stats, setStats] = useState({
    totalUsers: 0,
    customers: 0,
    admins: 0,
    managers: 0,
    drivers: 0,
    pickers: 0,
    packers: 0,
    thisWeek: 0,
    thisMonth: 0
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Fetch all users from user_profiles
      const { data: usersData, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch default addresses for all users
      const userIds = usersData?.map(u => u.id) || [];
      let addressMap = {};
      if (userIds.length > 0) {
        const { data: addressesData } = await supabase
          .from('addresses')
          .select('*')
          .in('user_id', userIds)
          .eq('is_default', true);
        
        addressesData?.forEach(addr => {
          addressMap[addr.user_id] = addr;
        });
      }

      // Attach addresses to users
      const usersWithAddresses = usersData?.map(user => ({
        ...user,
        default_address: addressMap[user.id] || null
      })) || [];

      setUsers(usersWithAddresses);

      // Calculate stats
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const statsData = {
        totalUsers: usersData?.length || 0,
        customers: usersData?.filter(u => u.role === 'customer').length || 0,
        admins: usersData?.filter(u => u.role === 'admin').length || 0,
        managers: usersData?.filter(u => u.role === 'manager').length || 0,
        drivers: usersData?.filter(u => u.role === 'driver').length || 0,
        pickers: usersData?.filter(u => u.role === 'picker').length || 0,
        packers: usersData?.filter(u => u.role === 'packer').length || 0,
        thisWeek: usersData?.filter(u => new Date(u.created_at) >= weekAgo).length || 0,
        thisMonth: usersData?.filter(u => new Date(u.created_at) >= monthAgo).length || 0
      };

      setStats(statsData);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Failed to load users: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-700',
      manager: 'bg-blue-100 text-blue-700',
      customer: 'bg-green-100 text-green-700',
      driver: 'bg-orange-100 text-orange-700',
      picker: 'bg-yellow-100 text-yellow-700',
      packer: 'bg-indigo-100 text-indigo-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[role] || 'bg-gray-100 text-gray-700'}`}>
        {role || 'Unknown'}
      </span>
    );
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    // Search filter
    const matchesSearch = !searchQuery || 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());

    // Role filter
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;

    // Date filter
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const createdDate = new Date(user.created_at);
      const now = new Date();
      if (dateFilter === 'today') {
        matchesDate = createdDate.toDateString() === now.toDateString();
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        matchesDate = createdDate >= weekAgo;
      } else if (dateFilter === 'month') {
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        matchesDate = createdDate >= monthAgo;
      }
    }

    return matchesSearch && matchesRole && matchesDate;
  });

  // Get unique roles for filter dropdown
  const uniqueRoles = [...new Set(users.map(u => u.role).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Users Management</h1>
          <p className="text-gray-600 mt-1">View and manage all registered users</p>
        </div>
        <button
          onClick={fetchUsers}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users size={20} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Customers</p>
              <p className="text-2xl font-bold text-green-600">{stats.customers}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <ShoppingCart size={20} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Admins</p>
              <p className="text-2xl font-bold text-purple-600">{stats.admins}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Shield size={20} className="text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Drivers</p>
              <p className="text-2xl font-bold text-orange-600">{stats.drivers}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Truck size={20} className="text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">This Week</p>
              <p className="text-2xl font-bold text-indigo-600">{stats.thisWeek}</p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-lg">
              <Clock size={20} className="text-indigo-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-sm text-gray-600">Managers</p>
          <p className="text-xl font-bold text-blue-600">{stats.managers}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-sm text-gray-600">Pickers</p>
          <p className="text-xl font-bold text-yellow-600">{stats.pickers}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-sm text-gray-600">Packers</p>
          <p className="text-xl font-bold text-indigo-600">{stats.packers}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-sm text-gray-600">This Month</p>
          <p className="text-xl font-bold text-teal-600">{stats.thisMonth}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          {/* Role Filter */}
          <div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="all">All Roles</option>
              {uniqueRoles.map(role => (
                <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
              ))}
            </select>
          </div>

          {/* Date Filter */}
          <div>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>

          {/* Results count */}
          <div className="text-sm text-gray-600">
            Showing {filteredUsers.length} of {users.length} users
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No users found matching your filters
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <span className="text-green-600 font-semibold">
                            {(user.name || user.email || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium text-gray-900">{user.name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-gray-600">{user.email}</div>
                      {user.default_address && (
                        <div className="text-xs text-gray-400 mt-1">
                          {user.default_address.physical_address}
                          {user.default_address.complex_name && `, ${user.default_address.complex_name}`}
                          {user.default_address.unit_number && ` (${user.default_address.unit_number})`}
                          {user.default_address.area && ` - ${user.default_address.area}`}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-600">{user.phone || '-'}</td>
                    <td className="px-6 py-4">{getRoleBadge(user.role)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{formatDate(user.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ========================================
// PERMISSIONS MANAGEMENT SCREEN
// ========================================
const PermissionsManagementScreen = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [expandedAdmin, setExpandedAdmin] = useState(null);

  // Permissions grouped by menu structure
  const PERMISSION_GROUPS = [
    {
      name: 'Master Data',
      icon: Database,
      permissions: [
        { key: 'products', label: 'Products' },
        { key: 'categories', label: 'Categories' },
        { key: 'subcategories', label: 'Sub-Categories' },
        { key: 'variants', label: 'Variants' },
        { key: 'promotions', label: 'Promotions' },
        { key: 'home_banners', label: 'Home Banners' },
        { key: 'product_images', label: 'Product Images' },
      ]
    },
    {
      name: 'Operations',
      icon: Package,
      permissions: [
        { key: 'picking', label: 'Picking' },
        { key: 'packing', label: 'Packing' },
        { key: 'delivery_boxes', label: 'Delivery Boxes' },
        { key: 'box_receiving', label: 'Box Receiving' },
        { key: 'warehouse', label: 'Warehouse Management' },
      ]
    },
    {
      name: 'Drivers',
      icon: Users,
      permissions: [
        { key: 'driver_applications', label: 'Driver Applications' },
        { key: 'drivers_management', label: 'Drivers Management' },
        { key: 'delivery_management', label: 'Delivery Management' },
        { key: 'driver_tracking', label: 'Driver Tracking' },
        { key: 'driver_payments', label: 'Driver Payments' },
      ]
    },
    {
      name: 'Transactions',
      icon: BarChart3,
      permissions: [
        { key: 'orders', label: 'Orders' },
        { key: 'order_capture', label: 'Order Capture' },
        { key: 'wallet_approvals', label: 'Wallet Approvals' },
        { key: 'document_flow', label: 'Document Flow' },
      ]
    },
    {
      name: 'Reports',
      icon: FileSpreadsheet,
      permissions: [
        { key: 'reports', label: 'VAT Report' },
      ]
    },
    {
      name: 'Other',
      icon: Settings,
      permissions: [
        { key: 'password_resets', label: 'Password Resets' },
        { key: 'permissions', label: 'Permissions' },
        { key: 'users', label: 'Users' },
        { key: 'settings', label: 'Settings' },
      ]
    },
  ];

  // Flat list of all permissions for grant/revoke all
  const ALL_PERMISSIONS = PERMISSION_GROUPS.flatMap(g => g.permissions);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      // Fetch all users that can have permissions assigned
      const { data: users, error: usersError } = await supabase
        .from('user_profiles')
        .select('id, email, name, role')
        .in('role', ['owner', 'admin', 'manager', 'picker', 'checker_packer', 'warehouse', 'security', 'driver'])
        .order('role')
        .order('email');

      if (usersError) throw usersError;

      // Fetch all permissions
      const { data: permissions, error: permError } = await supabase
        .from('admin_permissions')
        .select('user_id, permission');

      if (permError) throw permError;

      // Map permissions to users
      const adminsWithPermissions = users.map(user => ({
        ...user,
        permissions: permissions
          .filter(p => p.user_id === user.id)
          .map(p => p.permission)
      }));

      setAdmins(adminsWithPermissions);
      // Auto-expand first admin
      if (adminsWithPermissions.length > 0) {
        setExpandedAdmin(adminsWithPermissions[0].id);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      alert('Failed to load admins: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = async (userId, permission) => {
    const admin = admins.find(a => a.id === userId);
    const hasPermission = admin.permissions.includes(permission);

    setSaving(userId + permission);
    try {
      if (hasPermission) {
        // Remove permission
        const { error } = await supabase
          .from('admin_permissions')
          .delete()
          .eq('user_id', userId)
          .eq('permission', permission);

        if (error) throw error;

        setAdmins(prev => prev.map(a => 
          a.id === userId 
            ? { ...a, permissions: a.permissions.filter(p => p !== permission) }
            : a
        ));
      } else {
        // Add permission
        const { error } = await supabase
          .from('admin_permissions')
          .insert({ user_id: userId, permission });

        if (error) throw error;

        setAdmins(prev => prev.map(a => 
          a.id === userId 
            ? { ...a, permissions: [...a.permissions, permission] }
            : a
        ));
      }
    } catch (error) {
      console.error('Error toggling permission:', error);
      alert('Failed to update permission: ' + error.message);
    } finally {
      setSaving(null);
    }
  };

  const toggleGroupPermissions = async (userId, group) => {
    const admin = admins.find(a => a.id === userId);
    const groupPermKeys = group.permissions.map(p => p.key);
    const hasAll = groupPermKeys.every(k => admin.permissions.includes(k));

    setSaving(userId + group.name);
    try {
      if (hasAll) {
        // Remove all group permissions
        const { error } = await supabase
          .from('admin_permissions')
          .delete()
          .eq('user_id', userId)
          .in('permission', groupPermKeys);

        if (error) throw error;

        setAdmins(prev => prev.map(a => 
          a.id === userId 
            ? { ...a, permissions: a.permissions.filter(p => !groupPermKeys.includes(p)) }
            : a
        ));
      } else {
        // Add missing group permissions
        const missingPerms = groupPermKeys.filter(k => !admin.permissions.includes(k));
        const { error } = await supabase
          .from('admin_permissions')
          .insert(missingPerms.map(permission => ({ user_id: userId, permission })));

        if (error) throw error;

        setAdmins(prev => prev.map(a => 
          a.id === userId 
            ? { ...a, permissions: [...new Set([...a.permissions, ...groupPermKeys])] }
            : a
        ));
      }
    } catch (error) {
      console.error('Error toggling group permissions:', error);
      alert('Failed to update permissions: ' + error.message);
    } finally {
      setSaving(null);
    }
  };

  const grantAllPermissions = async (userId) => {
    setSaving(userId + 'all');
    try {
      // Delete existing permissions
      await supabase
        .from('admin_permissions')
        .delete()
        .eq('user_id', userId);

      // Insert all permissions
      const { error } = await supabase
        .from('admin_permissions')
        .insert(ALL_PERMISSIONS.map(p => ({ user_id: userId, permission: p.key })));

      if (error) throw error;

      setAdmins(prev => prev.map(a => 
        a.id === userId 
          ? { ...a, permissions: ALL_PERMISSIONS.map(p => p.key) }
          : a
      ));
    } catch (error) {
      console.error('Error granting all permissions:', error);
      alert('Failed to grant permissions: ' + error.message);
    } finally {
      setSaving(null);
    }
  };

  const revokeAllPermissions = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke ALL permissions for this user?')) return;

    setSaving(userId + 'all');
    try {
      const { error } = await supabase
        .from('admin_permissions')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;

      setAdmins(prev => prev.map(a => 
        a.id === userId 
          ? { ...a, permissions: [] }
          : a
      ));
    } catch (error) {
      console.error('Error revoking permissions:', error);
      alert('Failed to revoke permissions: ' + error.message);
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Permissions Management</h1>
          <p className="text-gray-600 mt-1">Control what each admin user can access</p>
        </div>
      </div>

      <div className="space-y-4">
        {admins.map(admin => {
          const isExpanded = expandedAdmin === admin.id;
          
          return (
            <div key={admin.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Admin Header - Clickable to expand */}
              <button
                onClick={() => setExpandedAdmin(isExpanded ? null : admin.id)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <span className="text-green-700 font-semibold">
                      {(admin.name || admin.email).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">{admin.name || admin.email}</h3>
                    <p className="text-sm text-gray-500">{admin.email}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    admin.role === 'admin' ? 'bg-purple-100 text-purple-700' : 
                    admin.role === 'manager' ? 'bg-blue-100 text-blue-700' :
                    admin.role === 'picker' ? 'bg-yellow-100 text-yellow-700' :
                    admin.role === 'checker_packer' ? 'bg-orange-100 text-orange-700' :
                    admin.role === 'warehouse' ? 'bg-green-100 text-green-700' :
                    admin.role === 'security' ? 'bg-red-100 text-red-700' :
                    admin.role === 'driver' ? 'bg-cyan-100 text-cyan-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {admin.role}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">
                    {admin.permissions.length} / {ALL_PERMISSIONS.length} permissions
                  </span>
                  {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </div>
              </button>

              {/* Expanded Permissions */}
              {isExpanded && (
                <div className="border-t border-gray-100 p-4">
                  {/* Quick Actions */}
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => grantAllPermissions(admin.id)}
                      disabled={saving === admin.id + 'all'}
                      className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      Grant All
                    </button>
                    <button
                      onClick={() => revokeAllPermissions(admin.id)}
                      disabled={saving === admin.id + 'all'}
                      className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50"
                    >
                      Revoke All
                    </button>
                  </div>

                  {/* Permission Groups */}
                  <div className="space-y-4">
                    {PERMISSION_GROUPS.map(group => {
                      const GroupIcon = group.icon;
                      const groupPermKeys = group.permissions.map(p => p.key);
                      const grantedCount = groupPermKeys.filter(k => admin.permissions.includes(k)).length;
                      const hasAll = grantedCount === group.permissions.length;
                      const hasNone = grantedCount === 0;

                      return (
                        <div key={group.name} className="border border-gray-200 rounded-lg overflow-hidden">
                          {/* Group Header */}
                          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <GroupIcon size={18} className="text-gray-600" />
                              <span className="font-medium text-gray-900">{group.name}</span>
                              <span className="text-xs text-gray-500">
                                ({grantedCount}/{group.permissions.length})
                              </span>
                            </div>
                            <button
                              onClick={() => toggleGroupPermissions(admin.id, group)}
                              disabled={saving === admin.id + group.name}
                              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                hasAll
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : hasNone
                                  ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                              }`}
                            >
                              {hasAll ? 'Revoke All' : 'Grant All'}
                            </button>
                          </div>

                          {/* Group Permissions */}
                          <div className="p-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {group.permissions.map(perm => {
                              const hasPermission = admin.permissions.includes(perm.key);
                              const isSaving = saving === admin.id + perm.key;

                              return (
                                <button
                                  key={perm.key}
                                  onClick={() => togglePermission(admin.id, perm.key)}
                                  disabled={isSaving}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                                    hasPermission
                                      ? 'bg-green-100 text-green-700 border border-green-300'
                                      : 'bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300'
                                  } ${isSaving ? 'opacity-50' : ''}`}
                                >
                                  {hasPermission ? (
                                    <CheckSquare size={16} className="text-green-600 flex-shrink-0" />
                                  ) : (
                                    <Square size={16} className="text-gray-400 flex-shrink-0" />
                                  )}
                                  <span className="truncate">{perm.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ── Animated Origin360 lockup, the same one Origin360 signs you in with ──────
// Arcs draw, ticks pop, the needle sweeps up, then the wordmark and tagline rise.
// Reds are the SHIPPED brand red (#F04A46), not the splash file's #C8102E/#E03A3E,
// so this matches the app it sends you to. Skipped entirely for anyone who has asked
// for reduced motion — it is decoration on a notice, and nothing waits on it.
const O3_CSS = `
@keyframes o3-draw   { to { stroke-dashoffset: 0 } }
@keyframes o3-fade   { to { opacity: 1 } }
@keyframes o3-pop    { 0% { opacity: 0; transform: scale(.7) } 100% { opacity: 1; transform: scale(1) } }
@keyframes o3-sweep  { to { transform: rotate(45deg) } }
@keyframes o3-glow   { 0%, 100% { opacity: 0 } 50% { opacity: .45 } }
@keyframes o3-rise   { to { opacity: 1; transform: translateY(0) } }
@keyframes o3-spread { to { opacity: 1; letter-spacing: 4px } }
.o3-track  { opacity: 0; animation: o3-fade .5s ease .1s forwards }
.o3-arc    { stroke-dasharray: 320; stroke-dashoffset: 320; animation: o3-draw .7s cubic-bezier(.65,0,.35,1) forwards }
.o3-arc-a  { animation-delay: .10s }
.o3-arc-b  { animation-delay: .30s }
.o3-tick   { opacity: 0; transform-origin: 400px 300px; animation: o3-pop .3s cubic-bezier(.2,1.4,.4,1) forwards }
.o3-tick:nth-of-type(1) { animation-delay: .40s }
.o3-tick:nth-of-type(2) { animation-delay: .47s }
.o3-tick:nth-of-type(3) { animation-delay: .54s }
.o3-tick:nth-of-type(4) { animation-delay: .61s }
.o3-tick:nth-of-type(5) { animation-delay: .68s }
.o3-needle { transform-origin: 400px 300px; transform: rotate(-88deg); animation: o3-sweep 1.05s cubic-bezier(.68,-.15,.27,1.25) .75s forwards }
.o3-glow   { opacity: 0; animation: o3-glow 2.6s ease-in-out 1.9s infinite }
.o3-word   { opacity: 0; transform: translateY(16px); animation: o3-rise .6s cubic-bezier(.2,.8,.2,1) 1.15s forwards }
.o3-tag    { opacity: 0; letter-spacing: 14px; animation: o3-spread .9s cubic-bezier(.2,.8,.2,1) 1.5s forwards }
@media (prefers-reduced-motion: reduce) {
  .o3-track, .o3-arc, .o3-tick, .o3-needle, .o3-word, .o3-tag {
    animation: none; opacity: 1; transform: none; stroke-dashoffset: 0;
  }
  .o3-tag    { letter-spacing: 4px; transform: translateX(-50%) }
  .o3-needle { transform: rotate(45deg) }
  .o3-glow   { animation: none; opacity: 0 }
}
`;

// Main Dashboard Component
const AdminDashboard = () => {
  const { user, signOut, userProfile } = useAuth();
  const [currentScreen, setCurrentScreen] = useState('mdfrozen');
  // ── FULL DECOMMISSION. Every feature of this admin app now lives in Origin360,
  // which is the system of record. This app stays reachable on purpose — as a signpost, so
  // anyone with an old bookmark is told where the work moved instead of hitting a dead link.
  //
  // The freeze is an ALLOWLIST, not a blocklist. There are 34 screens in here; a blocklist
  // leaves anything it forgets wide open, and a screen left open is a second place where data
  // gets written and the two systems drift apart. So everything redirects to the notice unless
  // it is named in STILL_LIVE — which is empty, because nothing is left here.
  //
  // Reversible: set APP_FROZEN = false, or name a screen in STILL_LIVE to reopen just that one.
  const APP_FROZEN = true;
  const STILL_LIVE = [];
  useEffect(() => {
    if (APP_FROZEN && currentScreen !== 'mdfrozen' && !STILL_LIVE.includes(currentScreen)) {
      setCurrentScreen('mdfrozen');
    }
  }, [currentScreen]); // eslint-disable-line react-hooks/exhaustive-deps
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({
    masterData: true,
    operations: true,
    drivers: true,
    transactions: true,
    reports: true,
    manageUsers: true
  });
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCategories: 0,
    totalSubcategories: 0,
    activeDeals: 0,
    inStock: 0,
    outOfStock: 0
  });
  const [loading, setLoading] = useState(true);

  // Helper function to check if user has a permission
  const hasPermission = (permission) => {
    // Owner has all permissions
    if (userProfile?.role === 'owner') return true;
    // If permissions still loading, don't show anything yet
    if (permissionsLoading) return false;
    // Check if user has the specific permission
    return permissions.includes(permission);
  };

  // Fetch user permissions on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('admin_permissions')
          .select('permission')
          .eq('user_id', user.id);

        if (error) {
          console.error('Error fetching permissions:', error);
          // On error, allow full access as fallback
          setPermissions([]);
        } else {
          setPermissions(data.map(p => p.permission));
        }
      } catch (err) {
        console.error('Error fetching permissions:', err);
        setPermissions([]);
      } finally {
        setPermissionsLoading(false);
      }
    };

    fetchPermissions();
  }, [user]);

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // No longer fetch all products - ProductsScreen handles its own data via server-side search
      console.log('📦 Loading categories and stats...');

      // Fetch categories and stats only
      const [
        { data: categoriesData, error: categoriesError },
        { count: totalProductsCount },
        { count: subcategoriesCount },
        { count: inStockCount },
        { count: activeDealsCount }
      ] = await Promise.all([
        supabase.from('categories').select('*').order('name'),
        supabase.from('products').select('*', { count: 'exact', head: true }),
        supabase.from('subcategories').select('*', { count: 'exact', head: true }),
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('in_stock', true),
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('is_daily_deal', true)
      ]);

      if (categoriesError) throw categoriesError;

      setProducts([]); // Products are now loaded by ProductsScreen via server-side search
      setCategories(categoriesData || []);

      // Calculate stats from counts
      setStats({
        totalProducts: totalProductsCount || 0,
        totalCategories: categoriesData?.length || 0,
        totalSubcategories: subcategoriesCount || 0,
        activeDeals: activeDealsCount || 0,
        inStock: inStockCount || 0,
        outOfStock: (totalProductsCount || 0) - (inStockCount || 0)
      });

      console.log('✅ Categories and stats loaded successfully');
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to load data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      console.log('🔴 Logging out...');
      
      // Direct Supabase signout (bypass AuthContext FCM token clearing)
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('❌ Logout error:', error);
      }
      
      // Clear all local storage
      localStorage.clear();
      sessionStorage.clear();
      
      console.log('✅ Signed out successfully');
      
      // Force reload to clear state and show login
      window.location.href = '/';
      
    } catch (error) {
      console.error('❌ Logout error:', error);
      
      // Force clear and reload anyway
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/';
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-lg font-semibold text-gray-700">Loading all products...</p>
          <p className="text-sm text-gray-500">This may take a moment for large datasets</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar Toggle Button - visible when collapsed */}
      {sidebarCollapsed && (
        <button
          onClick={() => setSidebarCollapsed(false)}
          className="fixed left-4 top-4 z-50 p-2 bg-white border border-gray-200 rounded-lg shadow-lg hover:bg-gray-50 transition-colors"
          title="Show Menu"
        >
          <ChevronRight size={24} className="text-gray-600" />
        </button>
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full bg-white border-r border-gray-200 shadow-lg transition-all duration-300 z-40 ${
        sidebarCollapsed ? '-translate-x-full' : 'translate-x-0 w-64'
      }`}>
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Settings size={32} className="text-green-600" />
              </div>
              <div>
                <h1 className="font-bold text-lg text-gray-900">Foodline</h1>
                <p className="text-xs text-gray-500">Admin Dashboard</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarCollapsed(true)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              title="Hide Menu"
            >
              <X size={18} className="text-gray-500" />
            </button>
          </div>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          {/* Dashboard */}
          <button
            onClick={() => setCurrentScreen('dashboard')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              currentScreen === 'dashboard'
                ? 'bg-green-100 text-green-700 font-semibold'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Home size={20} />
            Dashboard
          </button>

          {/* TV Dashboard */}
          {hasPermission('document_flow') && (
          <button
            onClick={() => setCurrentScreen('docflowtv')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              currentScreen === 'docflowtv'
                ? 'bg-green-100 text-green-700 font-semibold'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Monitor size={20} />
            TV Dashboard
          </button>
          )}

          {/* Master Data - shows if user has any sub-permission */}
          {(hasPermission('products') || hasPermission('categories') || hasPermission('subcategories') || hasPermission('variants') || hasPermission('promotions') || hasPermission('home_banners') || hasPermission('product_images')) && (
          <div>
            <button
              onClick={() => toggleMenu('masterData')}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 font-medium"
            >
              <div className="flex items-center gap-3">
                <Database size={20} />
                Master Data
              </div>
              {expandedMenus.masterData ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            
            {expandedMenus.masterData && (
              <div className="ml-6 mt-1 space-y-1">
                {hasPermission('products') && (
                <button
                  onClick={() => setCurrentScreen('products')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'products'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Package size={18} />
                  Products
                </button>
                )}
                
                {hasPermission('categories') && (
                <button
                  onClick={() => setCurrentScreen('categories')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'categories'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Grid3x3 size={18} />
                  Categories
                </button>
                )}
                
                {hasPermission('subcategories') && (
                <button
                  onClick={() => setCurrentScreen('subcategories')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'subcategories'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Tag size={18} />
                  Sub-Categories
                </button>
                )}
                
                {hasPermission('variants') && (
                <button
                  onClick={() => setCurrentScreen('variants')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'variants'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Move size={18} />
                  Variants
                </button>
                )}
                
                {hasPermission('promotions') && (
                <button
                  onClick={() => setCurrentScreen('promotions')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'promotions'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Gift size={18} />
                  Promotions
                </button>
                )}

                {hasPermission('home_banners') && (
                <button
                  onClick={() => setCurrentScreen('homebanners')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'homebanners'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Image size={18} />
                  Home Banners
                </button>
                )}

                {hasPermission('product_images') && (
                <button
                  onClick={() => setCurrentScreen('imagemanager')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'imagemanager'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Upload size={18} />
                  Product Images
                </button>
                )}
              </div>
            )}
          </div>
          )}

          {/* Operations - shows if user has any sub-permission */}
          {(hasPermission('picking') || hasPermission('packing') || hasPermission('delivery_boxes') || hasPermission('box_receiving') || hasPermission('warehouse')) && (
          <div>
            <button
              onClick={() => toggleMenu('operations')}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 font-medium"
            >
              <div className="flex items-center gap-3">
                <Package size={20} />
                Operations
              </div>
              {expandedMenus.operations ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            
            {expandedMenus.operations && (
              <div className="ml-6 mt-1 space-y-1">
                {hasPermission('picking') && (
                <button
                  onClick={() => setCurrentScreen('picking')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'picking'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Truck size={18} />
                  Picking
                </button>
                )}
                
                {hasPermission('packing') && (
                <button
                  onClick={() => setCurrentScreen('packing')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'packing'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Box size={18} />
                  Packing
                </button>
                )}
                
                {hasPermission('delivery_boxes') && (
                <button
                  onClick={() => setCurrentScreen('deliveryboxes')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'deliveryboxes'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Box size={18} />
                  Delivery Boxes
                </button>
                )}
                
                {hasPermission('box_receiving') && (
                <button
                  onClick={() => setCurrentScreen('boxreceiving')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'boxreceiving'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Home size={18} />
                  Box Receiving
                </button>
                )}
                
                {hasPermission('warehouse') && (
                <>
                  <button
                    onClick={() => setCurrentScreen('stockoverview')}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                      currentScreen === 'stockoverview'
                        ? 'bg-green-100 text-green-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Layers size={18} />
                    Stock Overview
                  </button>
                  <button
                    onClick={() => setCurrentScreen('grvlist')}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                      currentScreen === 'grvlist'
                        ? 'bg-green-100 text-green-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <ClipboardList size={18} />
                    GRV List
                  </button>
                  <button
                    onClick={() => setCurrentScreen('stockregister')}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                      currentScreen === 'stockregister'
                        ? 'bg-green-100 text-green-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <FileText size={18} />
                    Stock Register
                  </button>
                </>
                )}
              </div>
            )}
          </div>
          )}

          {/* Drivers - shows if user has any sub-permission */}
          {(hasPermission('driver_applications') || hasPermission('drivers_management') || hasPermission('delivery_management') || hasPermission('driver_tracking') || hasPermission('driver_payments')) && (
          <div>
            <button
              onClick={() => toggleMenu('drivers')}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 font-medium"
            >
              <div className="flex items-center gap-3">
                <Users size={20} />
                Drivers
              </div>
              {expandedMenus.drivers ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            
            {expandedMenus.drivers && (
              <div className="ml-6 mt-1 space-y-1">
                {hasPermission('driver_applications') && (
                <button
                  onClick={() => setCurrentScreen('driverapplications')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'driverapplications'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Users size={18} />
                  Driver Applications
                </button>
                )}

                {hasPermission('drivers_management') && (
                <button
                  onClick={() => setCurrentScreen('driversmanagement')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'driversmanagement'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Truck size={18} />
                  Drivers Management
                </button>
                )}

                {hasPermission('delivery_management') && (
                <button
                  onClick={() => setCurrentScreen('deliverymanagement')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'deliverymanagement'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Package size={18} />
                  Delivery Management
                </button>
                )}

                {hasPermission('driver_tracking') && (
                <button
                  onClick={() => setCurrentScreen('drivertracking')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'drivertracking'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <MapPin size={18} />
                  Driver Tracking
                </button>
                )}

                {hasPermission('driver_payments') && (
                <button
                  onClick={() => setCurrentScreen('driverpayments')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'driverpayments'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Wallet size={18} />
                  Driver Payments
                </button>
                )}
              </div>
            )}
          </div>
          )}

          {/* Password Resets */}
          {hasPermission('password_resets') && (
          <button
            onClick={() => setCurrentScreen('passwordresets')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              currentScreen === 'passwordresets'
                ? 'bg-green-100 text-green-700 font-semibold'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Key size={20} />
            Password Resets
          </button>
          )}

          {/* Transactions - shows if user has any sub-permission */}
          {(hasPermission('orders') || hasPermission('wallet_approvals') || hasPermission('document_flow')) && (
          <div>
            <button
              onClick={() => toggleMenu('transactions')}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 font-medium"
            >
              <div className="flex items-center gap-3">
                <BarChart3 size={20} />
                Transactions
              </div>
              {expandedMenus.transactions ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            
            {expandedMenus.transactions && (
              <div className="ml-6 mt-1 space-y-1">
                {hasPermission('orders') && (
                <button
                  onClick={() => setCurrentScreen('orders')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'orders'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <ShoppingCart size={18} />
                  Orders
                </button>
                )}

                {/* Order Capture - Create orders for customers */}
                {hasPermission('order_capture') && (
                <button
                  onClick={() => setCurrentScreen('ordercapture')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'ordercapture'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Phone size={18} />
                  📞 Order Capture
                </button>
                )}
                
                {hasPermission('wallet_approvals') && (
                <button
                  onClick={() => setCurrentScreen('walletapprovals')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'walletapprovals'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Wallet size={18} />
                  Wallet Approvals
                </button>
                )}
                
                {/* Wallet Credit - Owner Only */}
                {userProfile?.role === 'owner' && (
                <button
                  onClick={() => setCurrentScreen('walletmanagement')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'walletmanagement'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Wallet size={18} />
                  💰 Wallet Credit
                </button>
                )}

                {/* Wallet Recon - Owner Only */}
                {userProfile?.role === 'owner' && (
                <button
                  onClick={() => setCurrentScreen('walletrecon')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'walletrecon'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Wallet size={18} />
                  📊 Wallet Recon
                </button>
                )}

                {/* BidVest Reconciliation Log - Owner Only */}
                {userProfile?.role === 'owner' && (
                <button
                  onClick={() => setCurrentScreen('reconlog')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'reconlog'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Check size={18} />
                  🔄 Reconciliation Log
                </button>
                )}

                {/* Sales Report - Owner Only */}
                {userProfile?.role === 'owner' && (
                <button
                  onClick={() => setCurrentScreen('salesreport')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'salesreport'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <BarChart3 size={18} />
                  📈 Sales Report
                </button>
                )}

                <button
                  onClick={() => setCurrentScreen('notifications')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'notifications'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Bell size={18} />
                  🔔 Notifications
                </button>
                
                {hasPermission('document_flow') && (
                <button
                  onClick={() => setCurrentScreen('docflow')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'docflow'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <FileText size={18} />
                  Document Flow
                </button>
                )}
              </div>
            )}
          </div>
          )}

          {/* Reports Section */}
          {hasPermission('reports') && (
          <div>
            <button
              onClick={() => toggleMenu('reports')}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 font-medium"
            >
              <div className="flex items-center gap-3">
                <FileSpreadsheet size={20} />
                Reports
              </div>
              {expandedMenus.reports ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            
            {expandedMenus.reports && (
              <div className="ml-6 mt-1 space-y-1">
                <button
                  onClick={() => setCurrentScreen('vatreport')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'vatreport'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <DollarSign size={18} />
                  VAT Report
                </button>
              </div>
            )}
          </div>
          )}

          {/* Manage Users Section */}
          {(hasPermission('permissions') || hasPermission('users')) && (
          <div>
            <button
              onClick={() => toggleMenu('manageUsers')}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 font-medium"
            >
              <div className="flex items-center gap-3">
                <Users size={20} />
                Manage Users
              </div>
              {expandedMenus.manageUsers ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            
            {expandedMenus.manageUsers && (
              <div className="ml-6 mt-1 space-y-1">
                {hasPermission('permissions') && (
                <button
                  onClick={() => setCurrentScreen('permissions')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'permissions'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Shield size={18} />
                  Permissions
                </button>
                )}
                
                {hasPermission('users') && (
                <button
                  onClick={() => setCurrentScreen('users')}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                    currentScreen === 'users'
                      ? 'bg-green-100 text-green-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Users size={18} />
                  Users
                </button>
                )}
              </div>
            )}
          </div>
          )}
        </nav>

        <button
          onClick={handleLogout}
          className="absolute bottom-6 left-6 right-6 flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <LogOut size={20} />
          Logout
        </button>
      </aside>

      {/* Main Content */}
      <main className={`transition-all duration-300 p-8 ${sidebarCollapsed ? 'ml-0' : 'ml-64'}`}>
        <div className="max-w-7xl mx-auto">
          {currentScreen === 'mdfrozen' && (
            <div className="flex items-center justify-center" style={{ minHeight: '70vh' }}>
              <div style={{ maxWidth: 620, width: '100%' }}>
                <div className="text-center">
                  {/* The real Origin360 lockup — same geometry AND the same entrance animation as
                      the sign-in screen: arcs draw, ticks pop, needle sweeps, wordmark and tagline
                      rise. The sign-in version sits on a dark panel, so its dial sweep and ticks
                      are near white; this panel is white, so those take navy and the hub gains a
                      navy ring, or the left half of the dial would be invisible.
                      The tagline is absolutely positioned in a fixed-height slot: its letter
                      spacing animates from 14px to 4px, and in normal flow that width change drags
                      the lockup's centre line and visibly slides the whole mark. */}
                  <div style={{ marginBottom: 22 }}>
                    <style>{O3_CSS}</style>
                    <svg
                      width="300"
                      height="183"
                      viewBox="175 80 450 275"
                      fill="none"
                      aria-label="Origin360"
                      style={{ display: 'block', margin: '0 auto' }}
                    >
                      <defs>
                        <linearGradient id="o3-red" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#F04A46" />
                          <stop offset="100%" stopColor="#D02F2B" />
                        </linearGradient>
                        <radialGradient id="o3-hub" cx="50%" cy="50%" r="50%">
                          <stop offset="0%" stopColor="#F04A46" stopOpacity=".8" />
                          <stop offset="100%" stopColor="#F04A46" stopOpacity="0" />
                        </radialGradient>
                      </defs>
                      <g transform="translate(0,10)">
                        <path className="o3-track" d="M 218 300 A 182 182 0 0 1 582 300" stroke="#243352" strokeWidth="6" strokeOpacity="0.35" />
                        <path className="o3-arc o3-arc-a" d="M 190 300 A 210 210 0 0 1 381.7 90.8" stroke="#243352" strokeWidth="22" strokeLinecap="round" />
                        <path className="o3-arc o3-arc-b" d="M 418.3 90.8 A 210 210 0 0 1 610 300" stroke="url(#o3-red)" strokeWidth="22" strokeLinecap="round" />
                        <g stroke="#243352" strokeWidth="8" strokeLinecap="round" strokeOpacity="0.55">
                          <line className="o3-tick" x1="257.1" y1="217.5" x2="239.8" y2="207.5" />
                          <line className="o3-tick" x1="317.5" y1="157.1" x2="307.5" y2="139.8" />
                          <line className="o3-tick" x1="400" y1="135" x2="400" y2="115" />
                          <line className="o3-tick" x1="482.5" y1="157.1" x2="492.5" y2="139.8" />
                          <line className="o3-tick" x1="542.9" y1="217.5" x2="560.2" y2="207.5" />
                        </g>
                        <circle className="o3-glow" cx="400" cy="300" r="40" fill="url(#o3-hub)" />
                        <g className="o3-needle">
                          <polygon points="391,306 409,306 402.5,152 397.5,152" fill="#F04A46" />
                        </g>
                        <circle cx="400" cy="300" r="26" fill="#FFFFFF" stroke="#243352" strokeWidth="4" />
                        <circle cx="400" cy="300" r="10" fill="#0E1830" />
                      </g>
                    </svg>

                    <div className="o3-word" style={{ marginTop: -6, fontSize: 46, fontWeight: 800, letterSpacing: '-0.5px', lineHeight: 1, whiteSpace: 'nowrap' }}>
                      <span style={{ color: '#0F1C2E' }}>Origin</span><span style={{ color: '#F04A46' }}>360</span>
                    </div>

                    <div style={{ position: 'relative', width: '100%', height: 16, marginTop: 16 }}>
                      <div
                        className="o3-tag"
                        style={{
                          position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 0,
                          fontSize: 12, fontWeight: 500, color: '#8B93A7', textTransform: 'uppercase',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Complete visibility. Maximum performance<span style={{ color: '#F04A46' }}>.</span>
                      </div>
                    </div>
                  </div>

                  <h1 className="text-2xl font-bold text-gray-900 mb-3">
                    Everything has moved to Origin360
                  </h1>
                  <p className="text-gray-600 mb-6" style={{ fontSize: 15, lineHeight: 1.6 }}>
                    This admin app is closed. Origin360 is now the system of record for all
                    of it — running one system means the numbers only exist in one place. Here is
                    where to find what you came for.
                  </p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden text-left">
                  {[
                    ['Products, categories, promos, banners', 'Products · Promotions'],
                    ['Stock, GRVs, stock register', 'Inventory · Goods Received'],
                    ['Orders, picking, packing, boxes, dispatch', 'Foodline → Fulfilment · Cockpit'],
                    ['Order dashboard and document flow', 'Foodline → Orders'],
                    ['Drivers, applications, payments, settlements', 'Delivery → Drivers · Settlement'],
                    ['Driver tracking', 'Delivery → Live Map'],
                    ['Customer notifications', 'Foodline → Notifications'],
                    ['Sales and VAT reports', 'Foodline → Sales Report · Sales & VAT'],
                    ['Wallet and payment reconciliation', 'Foodline → Reconciliation Hub'],
                    ['Admin users and permissions', 'Configuration → Users · Roles'],
                  ].map(([was, now], i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-4 px-4 py-3"
                      style={{ borderTop: i === 0 ? 'none' : '1px solid #F3F4F6' }}
                    >
                      <span className="text-gray-500" style={{ fontSize: 13.5 }}>{was}</span>
                      <span className="font-semibold text-gray-900 text-right" style={{ fontSize: 13.5 }}>
                        {now}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="text-center mt-6">
                  <a
                    href="https://origin-360.netlify.app"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 font-bold text-white rounded-xl px-6 py-3 hover:opacity-90 transition-opacity"
                    style={{ background: '#F04A46', fontSize: 15, textDecoration: 'none' }}
                  >
                    Open Origin360 →
                  </a>
                  <div className="text-gray-400 mt-2" style={{ fontSize: 12.5 }}>
                    origin-360.netlify.app
                  </div>
                </div>

                <p className="text-gray-400 text-center mt-5" style={{ fontSize: 12.5, lineHeight: 1.6 }}>
                  The menu on the left is left in place so old bookmarks still land somewhere useful.
                  Nothing here writes data any more. The customer app is unaffected — shoppers carry
                  on as normal.
                </p>
              </div>
            </div>
          )}

          {currentScreen === 'dashboard' && (
            <UnifiedDashboard />
          )}

          {currentScreen === 'products' && (
            <>
              <h1 className="text-3xl font-bold text-gray-900 mb-6">Products Management</h1>
              <ProductsScreen 
                products={products} 
                categories={categories} 
                onRefresh={fetchData}
                stats={stats}
              />
            </>
          )}

{currentScreen === 'variants' && (
      <VariantsScreen products={products} onRefresh={fetchData} />
    )}

{currentScreen === 'promotions' && (
  <PromotionsAdmin 
    externalProducts={products}
    externalCategories={categories}
    supabaseClient={supabase}
  />
)}

{/* HOME BANNERS SCREEN */}
{currentScreen === 'homebanners' && (
  <HomeBannerManager />
)}

{/* PRODUCT IMAGE MANAGER SCREEN */}
{currentScreen === 'imagemanager' && (
  <ProductImageManager />
)}

{/* NEW - ORDERS DASHBOARD SCREEN */}
{currentScreen === 'orders' && (
  <AdminOrderDashboard />
)}

{/* WALLET APPROVALS SCREEN */}
{currentScreen === 'walletapprovals' && (
  <WalletApprovalsScreen />
)}

{/* WALLET MANAGEMENT - OWNER ONLY */}
{currentScreen === 'walletmanagement' && (
  <AdminWalletManagement />
)}

{/* WALLET RECON - OWNER ONLY */}
{currentScreen === 'walletrecon' && (
  <AdminWalletRecon />
)}

{/* BIDVEST RECONCILIATION LOG - OWNER ONLY */}
{currentScreen === 'reconlog' && (
  <AdminReconciliationLog />
)}

{/* SALES REPORT - OWNER ONLY */}
{currentScreen === 'salesreport' && (
  <AdminSalesReport />
)}

{/* NOTIFICATIONS */}
{currentScreen === 'notifications' && (
  <AdminNotifications />
)}

{/* NEW - PICKING DASHBOARD SCREEN */}
{currentScreen === 'picking' && (
  <AdminPickingDashboard />
)}

{/* NEW - PACKING DASHBOARD SCREEN */}
{currentScreen === 'packing' && (
  <AdminPackingDashboard />
)}

{/* NEW - DOCUMENT FLOW DASHBOARD */}
{currentScreen === 'docflow' && (
  <DocumentFlowDashboard />
)}

{/* DOCUMENT FLOW TV DASHBOARD */}
{currentScreen === 'docflowtv' && (
  <DocumentFlowTVDashboard />
)}

{/* DELIVERY BOXES DASHBOARD */}
{currentScreen === 'deliveryboxes' && (
  <DeliveryBoxDashboard />
)}

{/* BOX RECEIVING DASHBOARD */}
{currentScreen === 'boxreceiving' && (
  <BoxReceivingDashboard />
)}

{/* STOCK OVERVIEW */}
{currentScreen === 'stockoverview' && (
  <AdminStockOverview />
)}

{/* GRV LIST */}
{currentScreen === 'grvlist' && (
  <AdminGRVList />
)}

{/* STOCK REGISTER */}
{currentScreen === 'stockregister' && (
  <AdminStockRegister />
)}

{/* DRIVER APPLICATIONS */}
{currentScreen === 'driverapplications' && (
  <DriverApplicationsAdmin />
)}

{/* DRIVERS MANAGEMENT */}
{currentScreen === 'driversmanagement' && (
  <DriversManagementAdmin />
)}

{/* DRIVER TRACKING MAP */}
{currentScreen === 'drivertracking' && (
  <AdminDriverTrackingMap />
)}

{/* DRIVER PAYMENTS */}
{currentScreen === 'driverpayments' && (
  <AdminDriverPayments />
)}

{/* DELIVERY MANAGEMENT */}
{currentScreen === 'deliverymanagement' && (
  <AdminDeliveryManagement />
)}

{/* PASSWORD RESET REQUESTS */}
{currentScreen === 'passwordresets' && (
  <PasswordResetRequestsAdmin />
)}

{/* PERMISSIONS MANAGEMENT */}
{currentScreen === 'permissions' && (
  <PermissionsManagementScreen />
)}

{/* USERS MANAGEMENT */}
{currentScreen === 'users' && (
  <UsersManagementScreen />
)}

{/* ORDER CAPTURE - Create orders for elderly customers */}
{currentScreen === 'ordercapture' && (
  <AdminOrderCapture onBack={() => setCurrentScreen('dashboard')} />
)}

{/* VAT REPORT */}
{currentScreen === 'vatreport' && (
  <VATReportScreen />
)}

          {currentScreen === 'categories' && (
            <CategoriesScreen categories={categories} onRefresh={fetchData} />
          )}

          {currentScreen === 'subcategories' && (
            <SubcategoriesScreen categories={categories} onRefresh={fetchData} />
          )}
        </div>
      </main>
    </div>
  );
};

// Main App Component
const AdminApp = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <AuthScreen />;
  }

  return <AdminDashboard />;
};


function App() {
  return (
    <AuthProvider>
      <AdminApp />
    </AuthProvider>
  );
}

export default App;
